import { type ChangeEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  AtSign,
  Bookmark,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  ExternalLink,
  Hash,
  Heart,
  Home,
  Image,
  Library,
  LogIn,
  LogOut,
  LockKeyhole,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Radio,
  RefreshCw,
  Repeat2,
  Search,
  Send,
  Shield,
  Share2,
  Smile,
  Tag,
  User,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import './styles.css'
import AuthDialog, { type AuthMember } from './AuthDialog'
import AdminModerationDialog from './AdminModerationDialog'
import ReplyNotifications from './ReplyNotifications'
import type { ReplyNotification } from './replyNotificationClient'
import { commentReplyInput, replyTargetForContext, type CommentReplyTarget } from './commentReplies'
import type { ModerationCompletion, ModerationTarget } from './adminModeration'
import CastProfileContent from './CastProfileContent'
import CastProfileHero from './CastProfileHero'
import CatalogEntityContent from './CatalogEntityContent'
import CastSpotlight from './CastSpotlight'
import Countdown from './Countdown'
import RoomCountdown from './RoomCountdown'
import EditProfileDialog from './EditProfileDialog'
import EpisodeShowPicker from './EpisodeShowPicker'
import LaunchCastHub from './LaunchCastHub'
import LiveRoomDirectory from './LiveRoomChooser'
import type { RoomSelection } from './liveRoomDirectory'
import { castPortraits, portraitForPerson } from './castPortraits'
import { profileForPerson } from './castProfiles'
import { launchShows } from './launchShows'
import { mergeLiveMessages, sameLiveRoomContext, type LiveRoomContext } from './liveMessages'
import { livePostingNotice } from './livePostingNotice'
import { roomGuidelines } from './roomGuidelines'
import { shuffledCopy } from './shuffle'
import { groupEpisodeOptions, type EpisodeSeasonOrder } from './episodeIndex'
import { catalogShowLabel, catalogShowsFromPayload, fetchCatalogPickerPage, type CatalogFilter, type CatalogResults, type CatalogShow } from './catalogShowPicker'
import { comingSoonFeatures } from './featureAvailability'
import type { PersonProfilePayload, ProfileEpisode } from './profileTypes'
import type { CatalogOption } from './types'
import { followKeys, quoteExcerpt, sharedPostFromHash, type QuoteExcerpt } from './socialPresentation'

const navItems = [
  { label: 'Home', icon: Home, active: true },
  { label: 'Live rooms', icon: Radio },
  { label: 'Couples', icon: Users },
  { label: 'Cast', icon: User },
  { label: 'Episodes', icon: Library },
  { label: 'Groups', icon: Hash, comingSoon: true },
  { label: 'Bookmarks', icon: Bookmark },
]

const CATALOG_PAGE_SIZE = 50

type Post = {
  id: string
  authorId: string
  name: string
  handle: string
  time: string
  avatar: string
  avatarUrl?: string | null
  tone: string
  body: string
  tags: string[]
  tagEntities?: CatalogOption[]
  likes: number
  replies: number
  reposts: number
  liked?: boolean
  bookmarked?: boolean
  quotePostId?: string | null
}

type Comment = {
  id: string
  parentCommentId: string | null
  body: string
  postedAt: string
  author: { id: string; name: string; handle: string; initials: string; avatarUrl?: string | null }
}

function commentFromPayload(row: Record<string, unknown>): Comment {
  const author = (row.author ?? {}) as Record<string, unknown>
  const name = String(author.display_name ?? 'Journey90 fan').replace(/ · Demo( fan)?$/, '')
  return {
    id: String(row.id), parentCommentId: typeof row.parent_comment_id === 'string' ? row.parent_comment_id : null,
    body: String(row.body ?? ''), postedAt: relativeTime(row.posted_at),
    author: { id: String(author.id ?? ''), name, handle: `@${String(author.handle ?? 'fan')}`, initials: initialsFor(name), avatarUrl: avatarUrlFromPayload(author) },
  }
}

type LiveMessage = {
  id: string
  authorId?: string
  initials: string
  avatarUrl?: string | null
  name: string
  handle?: string
  body: string
  tone: string
  sentAt?: string
  reactionCount?: number
  reacted?: boolean
  reactable?: boolean
}

type CurrentMember = {
  id: string
  name: string
  handle: string
  initials: string
  avatarUrl: string | null
  bio: string
  role?: string
}

type AuthIntent = 'composer' | 'following' | 'follow' | 'like' | 'live' | 'reply' | null

type CatalogPage = {
  results?: CatalogResults
  pagination?: {
    next_offset?: number
    has_more?: boolean
  }
}

type MentionContext = {
  start: number
  end: number
  query: string
}

function catalogOptionsFromResults(results: CatalogResults, includeEpisodes = true): CatalogOption[] {
  const relationshipOptions: CatalogOption[] = (results.relationships ?? []).map((row) => ({
    id: String(row.id),
    type: 'relationship',
    label: String(row.display_name),
    detail: `${String(row.first_show ?? 'Couple journey')}${row.first_season ? ` · Season ${row.first_season}` : ''} · ${row.status_review === 'published' ? String(row.status ?? 'Status published') : 'Status under review'}`,
    initials: String(row.display_name).split(/\s*&\s*|\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(),
    seasonCount: Number(row.appearance_count ?? 0),
  }))
  const personOptions: CatalogOption[] = (results.people ?? []).map((row) => ({
    id: String(row.id),
    type: 'person',
    label: String(row.name),
    detail: `${String(row.first_show ?? 'Cast member')}${row.first_season ? ` · Season ${row.first_season}` : ''}`,
    initials: String(row.name).split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(),
  }))
  const episodeOptions: CatalogOption[] = includeEpisodes ? (results.episodes ?? []).map((row) => ({
    id: String(row.id),
    type: 'episode',
    label: String(row.title),
    detail: `${String(row.series)} · S${row.season}${row.episode ? `E${row.episode}` : ' special'}`,
    initials: row.episode ? `E${row.episode}` : 'SP',
    series: String(row.series),
    season: Number(row.season),
    episode: row.episode == null ? null : Number(row.episode),
    airDate: typeof row.air_date === 'string' ? row.air_date : null,
  })) : []

  return [...relationshipOptions, ...personOptions, ...episodeOptions]
}

function mentionContextAt(value: string, caret: number | null): MentionContext | null {
  const caretIndex = caret ?? value.length
  const beforeCaret = value.slice(0, caretIndex)
  const match = beforeCaret.match(/(?:^|[\s([{"'“])@([^@\s,!?;:()\[\]{}]{0,40})$/)
  if (!match) return null
  const query = match[1] ?? ''
  const trailingToken = value.slice(caretIndex).match(/^[^@\s,!?;:()\[\]{}]*/)?.[0] ?? ''
  return { start: caretIndex - query.length - 1, end: caretIndex + trailingToken.length, query }
}

function rankMentionOptions(options: CatalogOption[], query: string) {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return options

  const score = (item: CatalogOption) => {
    const label = item.label.toLocaleLowerCase()
    if (label.startsWith(needle)) return 0
    if (label.split(/\s+|&/).some((word) => word.startsWith(needle))) return 1
    if (label.includes(needle)) return 2
    return 3
  }

  const featuredRank = (item: CatalogOption) => featuredCatalog.findIndex((featured) => featured.type === item.type && featured.id === item.id)
  return [...options].sort((left, right) => {
    const matchDifference = score(left) - score(right)
    if (matchDifference) return matchDifference
    const leftFeatured = featuredRank(left)
    const rightFeatured = featuredRank(right)
    if (leftFeatured >= 0 || rightFeatured >= 0) {
      if (leftFeatured < 0) return 1
      if (rightFeatured < 0) return -1
      return leftFeatured - rightFeatured
    }
    return left.label.localeCompare(right.label)
  })
}

const coreFeaturedCatalog: CatalogOption[] = [
  { id: 'ARC-B09532768188', type: 'relationship', label: 'Gino & Jasmine', detail: 'Couple journey · 6 season links · Status under review', initials: 'GJ', seasonCount: 6 },
  { id: 'ARC-7DD923A1934C', type: 'relationship', label: 'Kenneth & Armando', detail: 'Couple journey · The Other Way · Status under review', initials: 'KA', seasonCount: 3 },
  { id: 'ARC-554F65E5B213', type: 'relationship', label: 'Danielle & Mohamed', detail: 'Couple journey · 90 Day Fiancé season 2', initials: 'DM', seasonCount: 3 },
  { id: 'ARC-257C9F77A6EF', type: 'relationship', label: 'Big Ed & Liz', detail: 'Couple journey · The Single Life', initials: 'EL', seasonCount: 4 },
  { id: 'ARC-6E9479AA5D80', type: 'relationship', label: 'Rob & Sophie', detail: 'Couple journey · 90 Day Fiancé season 10', initials: 'RS', seasonCount: 3 },
  { id: 'p-jasmine-pineda', type: 'person', label: 'Jasmine Pineda', detail: 'Before the 90 Days', initials: 'JP' },
  { id: 'p-gino-palazzolo', type: 'person', label: 'Gino Palazzolo', detail: 'Before the 90 Days', initials: 'GP' },
  { id: 'TLR-S02E01', type: 'episode', label: 'Built to Last?', detail: 'The Last Resort · Season 2, Episode 1', initials: 'E1' },
  { id: 'B90-S05E01', type: 'episode', label: 'A Leap of Faith', detail: 'Before the 90 Days · Season 5, Episode 1', initials: 'E1' },
  { id: 'TOW-S02E01', type: 'episode', label: 'Home Is Where the Heart Is', detail: 'The Other Way · Season 2, Episode 1', initials: 'E1' },
]

const castCatalogOptions: CatalogOption[] = castPortraits.map((portrait) => ({
  id: portrait.personId,
  type: 'person',
  label: portrait.name,
  detail: portrait.showLabel,
  initials: portrait.initials,
}))

const spotlightPortraits = shuffledCopy(castPortraits)

const featuredCatalog: CatalogOption[] = [
  ...coreFeaturedCatalog,
  ...castCatalogOptions.filter((portrait) => !coreFeaturedCatalog.some((item) => item.id === portrait.id)),
]

function Avatar({ initials, tone = 'grape', small = false, imageUrl = null }: { initials: string; tone?: string; small?: boolean; imageUrl?: string | null }) {
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [imageUrl])
  return (
    <div className={`avatar avatar--${tone} ${small ? 'avatar--small' : ''}`} aria-hidden="true">
      {imageUrl && !imageFailed ? <img src={imageUrl} alt="" onError={() => setImageFailed(true)} /> : initials}
    </div>
  )
}

async function apiJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  const payload = await response.json().catch(() => ({})) as T & { message?: string }
  if (!response.ok) throw new Error(payload.message || 'Journey90 could not complete that request.')
  return payload
}

function relativeTime(value: unknown) {
  const timestamp = new Date(String(value ?? '')).getTime()
  if (!Number.isFinite(timestamp)) return 'now'
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1_000))
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}

function initialsFor(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'J9'
}

function avatarUrlFromPayload(member: AuthMember | Record<string, unknown>): string | null {
  const hasExplicitUrl = 'avatar_url' in member || 'avatarUrl' in member
  if (hasExplicitUrl) {
    const direct = member.avatar_url ?? member.avatarUrl
    return typeof direct === 'string' && direct.trim() ? direct : null
  }
  const id = String(member.id ?? '').trim()
  const version = member.avatar_version
  return id && version !== undefined && version !== null
    ? `/api/avatars/${encodeURIComponent(id)}?v=${encodeURIComponent(String(version))}`
    : null
}

function currentMemberFromPayload(member: AuthMember | Record<string, unknown>): CurrentMember {
  const name = String(member.display_name ?? member.displayName ?? 'Journey90 member').replace(/ · Demo( fan)?$/, '')
  return {
    id: String(member.id ?? ''),
    name,
    handle: `@${String(member.handle ?? 'member').replace(/^@/, '')}`,
    initials: initialsFor(name),
    avatarUrl: avatarUrlFromPayload(member),
    bio: typeof member.bio === 'string' ? member.bio : '',
    role: member.role ? String(member.role) : undefined,
  }
}

function canonicalPostTag(row: Record<string, unknown>): CatalogOption | null {
  const type = String(row.type ?? '')
  const id = String(row.id ?? '').trim()
  const label = String(row.label ?? '').trim()
  if ((type !== 'relationship' && type !== 'person' && type !== 'episode') || !id || !label) return null

  return {
    id,
    type,
    label,
    detail: type === 'relationship'
      ? 'Couple journey · Connected franchise library record'
      : type === 'person'
        ? 'Cast member · Connected franchise library record'
        : 'Episode · Connected franchise library record',
    initials: label.split(/\s*&\s*|\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase(),
  }
}

function postFromPayload(rawPost: Record<string, unknown>, index = 0): Post {
  const author = (rawPost.author ?? {}) as Record<string, unknown>
  const counts = (rawPost.counts ?? {}) as Record<string, unknown>
  const viewer = (rawPost.viewer ?? {}) as Record<string, unknown>
  const tagRows = Array.isArray(rawPost.tags) ? rawPost.tags as Array<Record<string, unknown>> : []
  const tagEntities = tagRows.map(canonicalPostTag).filter((tag): tag is CatalogOption => tag !== null)
  const name = String(author.display_name ?? 'Journey90 fan').replace(/ · Demo( fan)?$/, '')
  return {
    id: String(rawPost.id), authorId: String(author.id ?? ''), name,
    handle: `@${String(author.handle ?? 'fan')}`, time: relativeTime(rawPost.posted_at),
    avatar: initialsFor(name), avatarUrl: avatarUrlFromPayload(author),
    tone: ['coral', 'violet', 'grape'][index % 3], body: String(rawPost.body ?? ''),
    tags: tagEntities.map((tag) => tag.label), tagEntities,
    likes: Number(counts.reactions ?? 0), replies: Number(counts.comments ?? 0), reposts: Number(counts.reposts ?? 0),
    liked: Array.isArray(viewer.reactions) && viewer.reactions.includes('like'),
    bookmarked: storedIds('journey90:bookmarks').includes(String(rawPost.id)),
    quotePostId: typeof rawPost.quote_post_id === 'string' ? rawPost.quote_post_id : null,
  }
}

function storedIds(key: string) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) ?? '[]')
    return Array.isArray(value) ? value.map(String) : []
  } catch {
    return []
  }
}

function App() {
  const [feed, setFeed] = useState<'For you' | 'Following'>('For you')
  const [mobileSection, setMobileSection] = useState<'feed' | 'cast' | 'schedule'>('feed')
  const [draft, setDraft] = useState('')
  const [postItems, setPostItems] = useState<Post[]>([])
  const [selectedTags, setSelectedTags] = useState<CatalogOption[]>([])
  const [mentionContext, setMentionContext] = useState<MentionContext | null>(null)
  const [mentionOptions, setMentionOptions] = useState<CatalogOption[]>([])
  const [mentionLoading, setMentionLoading] = useState(false)
  const [activeMentionIndex, setActiveMentionIndex] = useState(0)
  const [panel, setPanel] = useState<'tags' | 'library' | 'entity' | 'rooms' | 'live' | 'comments' | null>(null)
  const [activeEntity, setActiveEntity] = useState<CatalogOption | null>(null)
  const [personProfile, setPersonProfile] = useState<PersonProfilePayload | null>(null)
  const [personProfileLoading, setPersonProfileLoading] = useState(false)
  const [personProfileError, setPersonProfileError] = useState('')
  const [activePost, setActivePost] = useState<Post | null>(null)
  const [catalogQuery, setCatalogQuery] = useState('')
  const [catalogFilter, setCatalogFilter] = useState<CatalogFilter>('people')
  const [catalogOptions, setCatalogOptions] = useState<CatalogOption[]>(featuredCatalog)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogLoadingMore, setCatalogLoadingMore] = useState(false)
  const [catalogOffset, setCatalogOffset] = useState(0)
  const [catalogHasMore, setCatalogHasMore] = useState(false)
  const [episodeSeasonOrder, setEpisodeSeasonOrder] = useState<EpisodeSeasonOrder>('newest')
  const [selectedEpisodeSeries, setSelectedEpisodeSeries] = useState<string | null>(null)
  const [catalogShows, setCatalogShows] = useState<CatalogShow[]>([])
  const [catalogShowsLoading, setCatalogShowsLoading] = useState(false)
  const [catalogShowsError, setCatalogShowsError] = useState('')
  const [catalogShowsRevision, setCatalogShowsRevision] = useState(0)
  const [catalogError, setCatalogError] = useState('')
  const [catalogRetryRevision, setCatalogRetryRevision] = useState(0)
  const [catalogTotals, setCatalogTotals] = useState<{ cast: number; relationships: number; episodes: number } | null>(null)
  const [globalQuery, setGlobalQuery] = useState('')
  const [globalMatches, setGlobalMatches] = useState<CatalogOption[]>([])
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false)
  const [globalSearchError, setGlobalSearchError] = useState(false)
  const [liveMessage, setLiveMessage] = useState('')
  const [liveMessages, setLiveMessages] = useState<LiveMessage[]>([
    { id: 'welcome-1', initials: 'J9', name: 'Journey90', body: 'Member chat opens 1 hour before airtime. Bring your own legal stream and react together here.', tone: 'grape' },
  ])
  const [activeLiveShow, setActiveLiveShow] = useState<RoomSelection>(launchShows[0])
  const [liveRoomStatus, setLiveRoomStatus] = useState<'pre' | 'live' | 'post' | 'locked'>('pre')
  const [liveCanPost, setLiveCanPost] = useState(false)
  const [liveRoomLoaded, setLiveRoomLoaded] = useState(false)
  const [liveAdminAccess, setLiveAdminAccess] = useState(false)
  const [livePostingOpensAt, setLivePostingOpensAt] = useState<string | null>(null)
  const [livePostingLocksAt, setLivePostingLocksAt] = useState<string | null>(null)
  const [livePublicCanPost, setLivePublicCanPost] = useState(false)
  const [liveRefreshRevision, setLiveRefreshRevision] = useState(0)
  const [liveSending, setLiveSending] = useState(false)
  const [liveReacting, setLiveReacting] = useState(false)
  const [feedLoading, setFeedLoading] = useState(false)
  const [feedError, setFeedError] = useState('')
  const [postBusy, setPostBusy] = useState(false)
  const [quotePost, setQuotePost] = useState<Post | null>(null)
  const [quotedPosts, setQuotedPosts] = useState<Record<string, QuoteExcerpt | null>>({})
  const [sharedPostId, setSharedPostId] = useState(() => sharedPostFromHash(window.location.hash))
  const [sharedPostError, setSharedPostError] = useState('')
  const [optionsPostId, setOptionsPostId] = useState<string | null>(null)
  const [editPostBody, setEditPostBody] = useState('')
  const [confirmPostDelete, setConfirmPostDelete] = useState(false)
  const [postActionBusy, setPostActionBusy] = useState(false)
  const [commentBusy, setCommentBusy] = useState(false)
  const [confirmCommentDelete, setConfirmCommentDelete] = useState<string | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentDraft, setCommentDraft] = useState('')
  const [commentReplyTarget, setCommentReplyTarget] = useState<CommentReplyTarget | null>(null)
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null)
  const [commentsError, setCommentsError] = useState('')
  const [followedTargets, setFollowedTargets] = useState<Set<string>>(() => new Set())
  const [currentMember, setCurrentMember] = useState<CurrentMember | null>(null)
  const [sessionStatus, setSessionStatus] = useState<'checking' | 'signed-out' | 'signed-in'>('checking')
  const [authOpen, setAuthOpen] = useState(false)
  const [incompleteMember, setIncompleteMember] = useState<AuthMember | null>(null)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [editProfileOpen, setEditProfileOpen] = useState(false)
  const [pendingAuthIntent, setPendingAuthIntent] = useState<AuthIntent>(null)
  const [feedRevision, setFeedRevision] = useState(0)
  const [moderationOpen, setModerationOpen] = useState(false)
  const [moderationTarget, setModerationTarget] = useState<ModerationTarget | null>(null)
  const [moderationRevision, setModerationRevision] = useState(0)
  const [toast, setToast] = useState('')
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const commentReplyRef = useRef<HTMLTextAreaElement>(null)
  const liveCursorRef = useRef('')
  const liveContextRef = useRef<LiveRoomContext>({ generation: 0, slug: '', memberId: '' })
  const liveReactionVersionRef = useRef(0)
  const livePendingReactionRef = useRef<{ context: LiveRoomContext; messageId: string; active: boolean; count: number } | null>(null)
  const followMutationVersionRef = useRef(0)
  const catalogRequestVersionRef = useRef(0)
  const catalogLoadingMoreRef = useRef(false)
  const catalogControllerRef = useRef<AbortController | null>(null)
  const catalogMoreControllerRef = useRef<AbortController | null>(null)
  const catalogShowsVersionRef = useRef(0)
  const selectedShowButtonRef = useRef<HTMLButtonElement | null>(null)
  const catalogShowFocusPendingRef = useRef(false)
  const currentMemberRef = useRef<CurrentMember | null>(null)
  const pendingAuthActionRef = useRef<(() => void) | null>(null)
  const scrolledSharedPostRef = useRef<string | null>(null)
  const commentsContextRef = useRef({version:0,postId:'',memberId:''})
  const notificationOpenVersionRef = useRef(0)
  const scrolledNotificationCommentRef = useRef<string | null>(null)
  const followedMemberId = currentMember?.id ?? null
  const selectedCommentReply = replyTargetForContext(commentReplyTarget, activePost?.id ?? '', currentMember?.id ?? '')
  const quotedPostIds = [...new Set(postItems.map((post) => post.quotePostId).filter((id): id is string => Boolean(id)))].sort().join(',')

  const catalogMatches = catalogOptions
  const episodeGroups = useMemo(() => groupEpisodeOptions(catalogOptions, episodeSeasonOrder), [catalogOptions, episodeSeasonOrder])
  const mentionQuery = mentionContext?.query ?? null
  const activePortrait = activeEntity?.type === 'person' ? portraitForPerson(activeEntity.id) : undefined
  const activeEditorialProfile = activeEntity?.type === 'person' ? profileForPerson(activeEntity.id) : undefined
  const latestReactableMessage = [...liveMessages].reverse().find((message) => message.reactable)

  useEffect(() => {
    setModerationOpen(false)
    setModerationTarget(null)
  }, [currentMember?.id, currentMember?.role])

  useEffect(() => {
    const query = globalQuery.trim()
    setGlobalMatches([])
    setGlobalSearchError(false)
    if (!query) { setGlobalSearchLoading(false); return }
    const controller = new AbortController()
    setGlobalSearchLoading(true)
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: query, type: 'all', limit: '5' })
      apiJson<CatalogPage>(`/api/catalog?${params}`, { signal: controller.signal })
        .then(data => { if (!controller.signal.aborted) setGlobalMatches(rankMentionOptions(catalogOptionsFromResults(data.results ?? {}), query).slice(0, 8)) })
        .catch((error: Error) => { if (!controller.signal.aborted && error.name !== 'AbortError') setGlobalSearchError(true) })
        .finally(() => { if (!controller.signal.aborted) setGlobalSearchLoading(false) })
    }, 140)
    return () => { window.clearTimeout(timer); controller.abort() }
  }, [globalQuery])

  useEffect(() => {
    const controller = new AbortController()
    apiJson<{ cast: number; relationships: number; episodes: number }>('/api/catalog-stats', { signal: controller.signal })
      .then((totals) => setCatalogTotals(totals))
      .catch(() => { /* Do not present stale hard-coded totals as verified counts. */ })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(''), 2600)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    let cancelled = false
    setFeedError('')
    const hydrateAuthenticatedMember = async () => {
      let session: { authenticated: boolean; needsProfileSetup?: boolean; member?: Record<string, unknown> | null }
      try {
        session = await apiJson('/api/session')
      } catch {
        if (!cancelled) {
          currentMemberRef.current = null
          setCurrentMember(null)
          setSessionStatus('signed-out')
        }
        return
      }
      if (cancelled) return
      if (!session.authenticated || !session.member) {
        currentMemberRef.current = null
        setCurrentMember(null)
        setSessionStatus('signed-out')
        return
      }
      if (session.needsProfileSetup) {
        setIncompleteMember(session.member as AuthMember)
        currentMemberRef.current = null
        setCurrentMember(null)
        setSessionStatus('signed-out')
        setAuthOpen(true)
        return
      }

      const hydratedMember = currentMemberFromPayload(session.member)
      currentMemberRef.current = hydratedMember
      setCurrentMember(hydratedMember)
      setIncompleteMember(null)
      setSessionStatus('signed-in')
    }

    void hydrateAuthenticatedMember()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const savedBookmarks = storedIds('journey90:bookmarks')
    if (savedBookmarks.length) {
      const bookmarks = new Set(savedBookmarks)
      setPostItems((current) => current.map((post) => ({ ...post, bookmarked: bookmarks.has(post.id) })))
    }
  }, [])

  // Hydrate after both initial session loading and returning email login.
  // Follow state belongs to the member, not a shared browser-local cache.
  useEffect(() => {
    setFollowedTargets(new Set())
    if (!followedMemberId) return
    const controller = new AbortController()
    const mutationVersion = followMutationVersionRef.current
    apiJson<{members?: Array<Record<string, unknown>>; entities?: Array<Record<string, unknown>>}>('/api/follows?limit=200', {signal: controller.signal})
      .then((payload) => {
        if (!controller.signal.aborted && mutationVersion === followMutationVersionRef.current) setFollowedTargets(followKeys(payload))
      })
      .catch((error: Error) => { if (error.name !== 'AbortError') setToast('Your follows could not load. Refresh to try again.') })
    return () => controller.abort()
  }, [followedMemberId])

  useEffect(() => {
    setQuotedPosts({})
    if (!quotedPostIds) { setQuotedPosts({}); return }
    const controller = new AbortController()
    void Promise.all(quotedPostIds.split(',').map(async (id) => {
      try {
        const payload = await apiJson<{post: Record<string, unknown>}>(`/api/posts/${encodeURIComponent(id)}`, {signal: controller.signal})
        return [id, quoteExcerpt(payload.post)] as const
      } catch { return [id, null] as const }
    })).then((entries) => { if (!controller.signal.aborted) setQuotedPosts(Object.fromEntries(entries)) })
    return () => controller.abort()
  }, [quotedPostIds, followedMemberId, moderationRevision])

  useEffect(() => {
    const handleHash = () => {
      scrolledSharedPostRef.current = null
      const postId = sharedPostFromHash(window.location.hash)
      setSharedPostId(postId)
      if (postId) {
        setMobileSection('feed')
        setPanel(null)
        setAccountMenuOpen(false)
      }
    }
    window.addEventListener('hashchange', handleHash)
    return () => window.removeEventListener('hashchange', handleHash)
  }, [])

  useEffect(() => {
    setSharedPostError('')
    if (!sharedPostId || sessionStatus === 'checking') return
    const controller = new AbortController()
    apiJson<{post: Record<string, unknown>}>(`/api/posts/${encodeURIComponent(sharedPostId)}`, {signal: controller.signal})
      .then(({post}) => {
        if (!controller.signal.aborted) setPostItems((current) => current.some((item) => item.id === sharedPostId) ? current : [postFromPayload(post), ...current])
      })
      .catch((error: Error) => { if (error.name !== 'AbortError') setSharedPostError('That shared post is unavailable or requires access.') })
    return () => controller.abort()
  }, [sharedPostId, sessionStatus, followedMemberId, feedLoading, moderationRevision])

  useEffect(() => {
    if (mobileSection !== 'feed' || !sharedPostId || feedLoading || scrolledSharedPostRef.current === sharedPostId || !postItems.some((item) => item.id === sharedPostId)) return
    const target = document.getElementById(`post-${sharedPostId}`)
    if (!target) return
    scrolledSharedPostRef.current = sharedPostId
    const frame = window.requestAnimationFrame(() => { target.scrollIntoView({block:'center'}); target.focus({preventScroll:true}) })
    return () => window.cancelAnimationFrame(frame)
  }, [postItems, feedLoading, sharedPostId, mobileSection])

  useEffect(() => {
    if (panel !== 'entity' || activeEntity?.type !== 'person') {
      setPersonProfile(null)
      setPersonProfileLoading(false)
      setPersonProfileError('')
      return
    }

    const controller = new AbortController()
    setPersonProfile(null)
    setPersonProfileLoading(true)
    setPersonProfileError('')
    apiJson<PersonProfilePayload>(`/api/profiles/${encodeURIComponent(activeEntity.id)}`, { signal: controller.signal })
      .then((profile) => setPersonProfile(profile))
      .catch((error: Error) => {
        if (error.name !== 'AbortError') setPersonProfileError('The episode guide is taking a short commercial break. Please try this profile again in a moment.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setPersonProfileLoading(false)
      })

    return () => controller.abort()
  }, [activeEntity, panel])

  useEffect(() => {
    let cancelled = false
    if (feed === 'Following' && sessionStatus === 'checking') return
    if (feed === 'Following' && !currentMember) {
      setFeedLoading(false)
      setPostItems([])
      return
    }
    setFeedLoading(true)
    const mutationVersion = followMutationVersionRef.current
    const scope = feed === 'Following' ? 'following' : 'for-you'
    apiJson<{ posts?: Array<Record<string, unknown>> }>(`/api/feed?scope=${scope}&limit=20`)
      .then((data: { posts?: Array<Record<string, unknown>> }) => {
        if (cancelled || mutationVersion !== followMutationVersionRef.current || !Array.isArray(data.posts)) return
        const backendFollows = new Set<string>()
        const mappedPosts = data.posts.map((rawPost, index) => {
          const author = (rawPost.author ?? {}) as Record<string, unknown>
          const viewer = (rawPost.viewer ?? {}) as Record<string, unknown>
          const authorId = String(author.id ?? '')
          if (viewer.follows_author && authorId) backendFollows.add(`member:${authorId}`)
          return postFromPayload(rawPost, index)
        })
        setPostItems(mappedPosts)
        if (backendFollows.size) setFollowedTargets((current) => new Set([...current, ...backendFollows]))
      })
      .catch(() => {
        if (!cancelled) {
          setPostItems([])
          setFeedError('The feed could not load. Use Newest to try again.')
        }
      })
      .finally(() => { if (!cancelled) setFeedLoading(false) })
    return () => { cancelled = true }
  }, [currentMember, feed, feedRevision, sessionStatus])

  useEffect(() => {
    const requestVersion = ++catalogShowsVersionRef.current
    const controller = new AbortController()
    setCatalogShows([])
    setCatalogShowsError('')
    setCatalogShowsLoading(false)
    setSelectedEpisodeSeries(null)
    if ((panel !== 'tags' && panel !== 'library') || catalogFilter !== 'episodes') return
    setCatalogShowsLoading(true)
    fetch('/api/catalog-shows', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('The show list could not load. Please retry.')))
      .then(payload => {
        if (controller.signal.aborted || requestVersion !== catalogShowsVersionRef.current) return
        setCatalogShows(catalogShowsFromPayload(payload))
      })
      .catch((error: Error) => {
        if (controller.signal.aborted || requestVersion !== catalogShowsVersionRef.current) return
        setCatalogShowsError(error.message || 'The show list could not load. Please retry.')
      })
      .finally(() => {
        if (!controller.signal.aborted && requestVersion === catalogShowsVersionRef.current) setCatalogShowsLoading(false)
      })
    return () => controller.abort()
  }, [catalogFilter, panel, catalogShowsRevision])

  useEffect(() => {
    if (!selectedEpisodeSeries) { catalogShowFocusPendingRef.current = false; return }
    if (catalogShowFocusPendingRef.current) {
      catalogShowFocusPendingRef.current = false
      selectedShowButtonRef.current?.focus({ preventScroll: true })
    }
  }, [selectedEpisodeSeries])

  useEffect(() => {
    const requestVersion = ++catalogRequestVersionRef.current
    catalogMoreControllerRef.current?.abort()
    catalogLoadingMoreRef.current = false
    setCatalogLoadingMore(false)
    setCatalogLoading(false)
    setCatalogHasMore(false)
    setCatalogOffset(0)
    setCatalogOptions([])
    setCatalogError('')
    if (panel !== 'tags' && panel !== 'library') return
    if (catalogFilter === 'episodes' && !selectedEpisodeSeries) return
    setCatalogLoading(true)
    const controller = new AbortController()
    catalogControllerRef.current = controller
    const timer = window.setTimeout(() => {
      const request = { filter: catalogFilter, query: catalogQuery, series: selectedEpisodeSeries, order: episodeSeasonOrder, limit: CATALOG_PAGE_SIZE, offset: 0 }
      fetchCatalogPickerPage(request, controller.signal)
        .then(data => {
          if (controller.signal.aborted || requestVersion !== catalogRequestVersionRef.current) return
          setCatalogOptions(catalogOptionsFromResults(data.results))
          setCatalogOffset(data.nextOffset)
          setCatalogHasMore(data.hasMore)
        })
        .catch((error: Error) => {
          if (controller.signal.aborted || requestVersion !== catalogRequestVersionRef.current) return
          setCatalogError(error.message || 'The index could not load. Please retry.')
        })
        .finally(() => {
          if (!controller.signal.aborted && requestVersion === catalogRequestVersionRef.current) setCatalogLoading(false)
        })
    }, 180)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
      catalogMoreControllerRef.current?.abort()
    }
  }, [catalogFilter, catalogQuery, selectedEpisodeSeries, episodeSeasonOrder, panel, catalogRetryRevision])

  function invalidateCatalogRequests() {
    ++catalogRequestVersionRef.current
    catalogControllerRef.current?.abort()
    catalogMoreControllerRef.current?.abort()
    catalogLoadingMoreRef.current = false
    setCatalogLoadingMore(false)
    setCatalogHasMore(false)
    setCatalogOffset(0)
    setCatalogOptions([])
    setCatalogError('')
  }

  function changeCatalogFilter(filter: CatalogFilter) {
    if (filter === catalogFilter) return
    invalidateCatalogRequests()
    setSelectedEpisodeSeries(null)
    setCatalogQuery('')
    setCatalogFilter(filter)
  }

  function chooseEpisodeSeries(series: string) {
    if (!catalogShows.some(show => show.series === series) || series === selectedEpisodeSeries) return
    invalidateCatalogRequests()
    catalogShowFocusPendingRef.current = true
    setCatalogQuery('')
    setSelectedEpisodeSeries(series)
  }

  async function loadMoreCatalog() {
    if ((panel !== 'tags' && panel !== 'library') || catalogLoading || !catalogHasMore || catalogLoadingMoreRef.current) return
    if (catalogFilter === 'episodes' && !selectedEpisodeSeries) return

    const requestVersion = catalogRequestVersionRef.current
    const requestedOffset = catalogOffset
    catalogLoadingMoreRef.current = true
    setCatalogLoadingMore(true)
    setCatalogError('')
    const controller = new AbortController()
    catalogMoreControllerRef.current = controller
    const request = { filter: catalogFilter, query: catalogQuery, series: selectedEpisodeSeries, order: episodeSeasonOrder, limit: CATALOG_PAGE_SIZE, offset: requestedOffset }

    try {
      const data = await fetchCatalogPickerPage(request, controller.signal)
      if (controller.signal.aborted || requestVersion !== catalogRequestVersionRef.current) return

      const incoming = catalogOptionsFromResults(data.results)
      setCatalogOptions((current) => {
        const known = new Set(current.map((item) => `${item.type}:${item.id}`))
        return [...current, ...incoming.filter((item) => !known.has(`${item.type}:${item.id}`))]
      })
      setCatalogOffset(data.nextOffset)
      setCatalogHasMore(data.hasMore)
    } catch (error) {
      if (!controller.signal.aborted && requestVersion === catalogRequestVersionRef.current) setCatalogError(error instanceof Error ? error.message : 'More results could not load. Please retry.')
    } finally {
      if (!controller.signal.aborted && requestVersion === catalogRequestVersionRef.current) {
        catalogLoadingMoreRef.current = false
        setCatalogLoadingMore(false)
      }
    }
  }

  function catalogResultButton(item: CatalogOption) {
    const chosen = selectedTags.some((tagItem) => tagItem.id === item.id)
    return (
      <button key={item.id} onClick={() => panel === 'tags' ? toggleTag(item) : openEntity(item)}>
        <span className={`result-icon result-icon--${item.type}`}>{item.initials}</span>
        <span><strong>{item.label}</strong><small>{item.detail}</small></span>
        {panel === 'tags' ? <span className={`tag-check ${chosen ? 'is-selected' : ''}`}>{chosen ? <Check size={15} /> : <Plus size={15} />}</span> : <ChevronRight size={17} />}
      </button>
    )
  }

  useEffect(() => {
    if (mentionQuery === null) {
      setMentionOptions([])
      setMentionLoading(false)
      return
    }

    if (!mentionQuery.trim()) {
      setMentionOptions(featuredCatalog.filter((item) => item.type !== 'episode').slice(0, 6))
      setMentionLoading(false)
      setActiveMentionIndex(0)
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setMentionLoading(true)
      const params = new URLSearchParams({ q: mentionQuery, type: 'all', limit: '8' })
      fetch(`/api/catalog?${params.toString()}`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Mention search unavailable')))
        .then((data: { results?: CatalogResults }) => {
          const matches = rankMentionOptions(catalogOptionsFromResults(data.results ?? {}, false), mentionQuery)
          setMentionOptions(matches.slice(0, 6))
          setActiveMentionIndex(0)
        })
        .catch((error: Error) => {
          if (error.name === 'AbortError') return
          const needle = mentionQuery.trim().toLocaleLowerCase()
          const matches = featuredCatalog.filter((item) => item.type !== 'episode' && (!needle || `${item.label} ${item.detail}`.toLocaleLowerCase().includes(needle)))
          setMentionOptions(rankMentionOptions(matches, mentionQuery).slice(0, 6))
          setActiveMentionIndex(0)
        })
        .finally(() => {
          if (!controller.signal.aborted) setMentionLoading(false)
        })
    }, 140)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [mentionQuery])

  useEffect(() => {
    if (!mentionContext || mentionOptions.length === 0) return
    document.getElementById(`composer-mention-${activeMentionIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeMentionIndex, mentionContext, mentionOptions.length])

  useEffect(() => {
    if (panel !== 'live') return
    let cancelled = false
    const controller = new AbortController()
    let poller: number | undefined
    const context = { generation: liveContextRef.current.generation + 1, slug: activeLiveShow.roomSlug, memberId: currentMember?.id ?? '' }
    liveContextRef.current = context
    liveCursorRef.current = ''
    livePendingReactionRef.current = null
    setLiveSending(false)
    setLiveReacting(false)
    setLiveCanPost(false)
    setLiveRoomLoaded(false)
    setLiveAdminAccess(false)
    setLivePostingOpensAt(null)
    setLivePostingLocksAt(null)
    setLivePublicCanPost(false)
    setLiveMessages([])

    const loadRoom = async () => {
      const cursor = liveCursorRef.current
      const reactionVersion = liveReactionVersionRef.current
      try {
        const params = new URLSearchParams({ slug: activeLiveShow.roomSlug, limit: '100' })
        if (cursor) params.set('after', cursor)
        const data = await apiJson<{
          room?: Record<string, unknown>
          messages?: Array<Record<string, unknown>>
          events?: Array<Record<string, unknown>>
          cursor?: string
          poll_after_ms?: number
        }>(`/api/live?${params.toString()}`, { signal: controller.signal })
        if (cancelled || !sameLiveRoomContext(context, liveContextRef.current)) return
        // A poll issued before a local reaction mutation may contain old state.
        // Leave its cursor unconsumed and fetch again rather than undo the save.
        if (reactionVersion !== liveReactionVersionRef.current) {
          poller = window.setTimeout(loadRoom, 1_500)
          return
        }
        const status = String(data.room?.state ?? 'pre')
        setLiveRoomStatus(status === 'live' || status === 'post' || status === 'locked' ? status : 'pre')
        setLiveCanPost(Boolean(data.room?.can_post))
        setLiveRoomLoaded(true)
        setLiveAdminAccess(data.room?.posting_access === 'admin')
        setLivePostingOpensAt(typeof data.room?.posting_opens_at === 'string' ? data.room.posting_opens_at : null)
        setLivePostingLocksAt(typeof data.room?.posting_locks_at === 'string' ? data.room.posting_locks_at : null)
        setLivePublicCanPost(Boolean(data.room?.public_can_post))
        const tones = ['coral', 'violet', 'grape']
        const incoming = (data.messages ?? []).map((message, index): LiveMessage => {
          const author = (message.author ?? {}) as Record<string, unknown>
          const name = String(author.display_name ?? 'Journey90 fan').replace(/ · Demo( fan)?$/, '')
          return {
            id: String(message.id ?? `${activeLiveShow.id}-${index}`),
            authorId: String(author.id ?? ''),
            initials: initialsFor(name),
            avatarUrl: avatarUrlFromPayload(author),
            name,
            handle: author.handle ? `@${String(author.handle)}` : undefined,
            body: String(message.body ?? ''),
            tone: tones[index % tones.length],
            sentAt: message.sent_at ? relativeTime(message.sent_at) : undefined,
            reactionCount: Number(message.reaction_count ?? 0),
            reacted: Boolean(message.viewer_reaction),
            reactable: true,
          }
        })
        const removedIds = new Set((data.events ?? [])
          .filter((event) => event.type === 'message_hidden' || event.type === 'message_removed')
          .map((event) => String(event.entity_id ?? '')))
        setLiveMessages((current) => {
          const pending = livePendingReactionRef.current
          const messages = incoming.map((message) => pending
            && sameLiveRoomContext(pending.context, context) && pending.messageId === message.id
            ? { ...message, reacted: pending.active, reactionCount: pending.count }
            : message)
          return mergeLiveMessages(cursor ? current : [], messages, removedIds)
        })
        liveCursorRef.current = String(data.cursor ?? cursor)
        poller = window.setTimeout(loadRoom, Math.max(1_500, Number(data.poll_after_ms ?? 4_000)))
      } catch {
        if (!cancelled && sameLiveRoomContext(context, liveContextRef.current)) {
          setLiveRoomStatus('pre')
          setLiveCanPost(false)
          setLiveRoomLoaded(false)
          setLiveAdminAccess(false)
          setLivePostingOpensAt(null)
          setLivePostingLocksAt(null)
          setLivePublicCanPost(false)
          setLiveMessages((current) => current.length ? current : [{ id: 'room-ready', initials: 'J9', name: 'Journey90', body: 'This room is being prepared for premiere night.', tone: 'grape' }])
          poller = window.setTimeout(loadRoom, 8_000)
        }
      }
    }

    void loadRoom()
    return () => {
      cancelled = true
      controller.abort()
      if (poller) window.clearTimeout(poller)
      if (sameLiveRoomContext(context, liveContextRef.current)) {
        liveContextRef.current = { generation: context.generation + 1, slug: '', memberId: '' }
        livePendingReactionRef.current = null
      }
    }
  }, [activeLiveShow, panel, currentMember?.id, liveRefreshRevision])

  useEffect(() => {
    setCommentReplyTarget(null)
    setCommentDraft('')
  }, [activePost?.id, currentMember?.id])

  useEffect(() => {
    if (panel !== 'comments' || !activePost) return
    let cancelled = false
    const context = {version:commentsContextRef.current.version + 1,postId:activePost.id,memberId:currentMember?.id ?? ''}
    commentsContextRef.current = context
    setComments([])
    setCommentsError('')
    setConfirmCommentDelete(null)
    setCommentsLoading(true)
    const controller = new AbortController()
    const targetQuery = highlightedCommentId ? `?target_comment_id=${encodeURIComponent(highlightedCommentId)}` : ''
    apiJson<{ comments?: Array<Record<string, unknown>>; selected_comment?: Record<string, unknown> | null }>(`/api/posts/${encodeURIComponent(activePost.id)}/comments${targetQuery}`, {signal:controller.signal})
      .then((data) => {
        if (cancelled) return
        const rows = data.comments ?? []
        if (highlightedCommentId && data.selected_comment?.id === highlightedCommentId && !rows.some((row) => row.id === highlightedCommentId)) rows.unshift(data.selected_comment)
        if (highlightedCommentId && data.selected_comment?.id !== highlightedCommentId) setCommentsError('That reply is no longer available. The remaining conversation is shown below.')
        setComments(rows.map(commentFromPayload))
      })
      .catch(() => { if (!cancelled) { setComments([]); setCommentsError('Replies could not load. Close and reopen this conversation to try again.') } })
      .finally(() => { if (!cancelled) setCommentsLoading(false) })
    return () => { cancelled = true; controller.abort(); if (commentsContextRef.current === context) commentsContextRef.current = {version:context.version + 1,postId:'',memberId:''} }
  }, [activePost, panel, currentMember?.id, moderationRevision, highlightedCommentId])

  useEffect(() => {
    if (panel !== 'comments' || commentsLoading || !highlightedCommentId || scrolledNotificationCommentRef.current === highlightedCommentId) return
    const target = document.getElementById(`comment-${highlightedCommentId}`)
    if (!target) return
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({block:'center',behavior:'auto'})
      target.focus({preventScroll:true})
      scrolledNotificationCommentRef.current = highlightedCommentId
    })
    return () => window.cancelAnimationFrame(frame)
  }, [panel, commentsLoading, comments, highlightedCommentId])

  function openEntity(item: CatalogOption) {
    setActiveEntity(item)
    setPanel('entity')
    setGlobalQuery('')
  }

  function openProfileRelationship(relationshipId: string) {
    const relationship = personProfile?.relationships.find((item) => item.id === relationshipId)
    const editorialJourney = activeEditorialProfile?.journeys.find((item) => item.relationshipId === relationshipId)
    const label = relationship?.display_name ?? editorialJourney?.label.replace(/^.*?:\s*/, '') ?? 'Couple journey'
    openEntity({
      id: relationshipId,
      type: 'relationship',
      label,
      detail: relationship?.first_show
        ? `${relationship.first_show}${relationship.first_season ? ` · Season ${relationship.first_season}` : ''}`
        : editorialJourney?.era ?? 'Connected relationship journey',
      initials: initialsFor(label.replace(/&/g, ' ')),
      seasonCount: relationship?.seasons.length ?? 0,
    })
  }

  function openProfilePerson(personId: string, name: string) {
    const connectedFrom = activeEntity?.type === 'person' ? activeEntity.label : 'this franchise journey'
    openEntity({
      id: personId,
      type: 'person',
      label: name,
      detail: `Cast member · Connected to ${connectedFrom}`,
      initials: initialsFor(name),
    })
  }

  function openProfileEpisode(episode: ProfileEpisode) {
    openEntity({
      id: episode.id,
      type: 'episode',
      label: episode.title,
      detail: `${episode.series} · Season ${episode.season}${episode.episode === null ? ' special' : ` · Episode ${episode.episode}`}`,
      initials: episode.episode === null ? 'SP' : `E${episode.episode}`,
    })
  }

  function beginAuth(intent: AuthIntent = null, resumeAction?: () => void) {
    setPendingAuthIntent(intent)
    pendingAuthActionRef.current = resumeAction ?? null
    setAccountMenuOpen(false)
    setAuthOpen(true)
  }

  function requireAuth(intent: Exclude<AuthIntent, null>, resumeAction?: () => void): CurrentMember | null {
    if (currentMemberRef.current) return currentMemberRef.current
    beginAuth(intent, resumeAction)
    return null
  }

  function finishAuthentication(member: AuthMember) {
    const authenticatedMember = currentMemberFromPayload(member)
    const intent = pendingAuthIntent
    const resumeAction = pendingAuthActionRef.current
    setPostItems([])
    setQuotedPosts({})
    currentMemberRef.current = authenticatedMember
    setCurrentMember(authenticatedMember)
    setIncompleteMember(null)
    setSessionStatus('signed-in')
    setAuthOpen(false)
    setPendingAuthIntent(null)
    pendingAuthActionRef.current = null
    setFeedRevision((current) => current + 1)
    setToast(`Welcome, ${authenticatedMember.name}`)
    if (intent === 'following') setFeed('Following')
    if (intent === 'composer') window.requestAnimationFrame(() => {
      composerRef.current?.focus()
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    if (resumeAction) window.requestAnimationFrame(resumeAction)
  }

  function applyProfileMember(member: Record<string, unknown>) {
    const updatedMember = currentMemberFromPayload(member)
    currentMemberRef.current = updatedMember
    setCurrentMember(updatedMember)
    setPostItems((current) => current.map((post) => post.authorId === updatedMember.id
      ? { ...post, name: updatedMember.name, handle: updatedMember.handle, avatar: updatedMember.initials, avatarUrl: updatedMember.avatarUrl }
      : post))
    setActivePost((current) => current?.authorId === updatedMember.id
      ? { ...current, name: updatedMember.name, handle: updatedMember.handle, avatar: updatedMember.initials, avatarUrl: updatedMember.avatarUrl }
      : current)
    setComments((current) => current.map((comment) => comment.author.id === updatedMember.id
      ? { ...comment, author: { ...comment.author, name: updatedMember.name, handle: updatedMember.handle, initials: updatedMember.initials, avatarUrl: updatedMember.avatarUrl } }
      : comment))
    setLiveMessages((current) => current.map((message) => message.authorId === updatedMember.id
      ? { ...message, name: updatedMember.name, handle: updatedMember.handle, initials: updatedMember.initials, avatarUrl: updatedMember.avatarUrl }
      : message))
  }

  async function signOut() {
    setAccountMenuOpen(false)
    setEditProfileOpen(false)
    try {
      await apiJson('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) })
    } catch (error) {
      setToast(error instanceof Error ? `Sign out failed: ${error.message}` : 'Sign out failed. Please try again.')
      return
    }
    currentMemberRef.current = null
    setPostItems([])
    setQuotedPosts({})
    setComments([])
    setActivePost(null)
    setQuotePost(null)
    setOptionsPostId(null)
    setCurrentMember(null)
    setIncompleteMember(null)
    setSessionStatus('signed-out')
    setFollowedTargets(new Set())
    followMutationVersionRef.current += 1
    setOptionsPostId(null)
    if (feed === 'Following') setFeed('For you')
    setFeedRevision((current) => current + 1)
    setToast('You’re signed out')
  }

  function openComposer() {
    setMobileSection('feed')
    setPanel(null)
    setAccountMenuOpen(false)
    if (!requireAuth('composer')) return
    window.requestAnimationFrame(() => {
      composerRef.current?.focus()
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  function toggleTag(item: CatalogOption) {
    const isSelected = selectedTags.some((tagItem) => tagItem.type === item.type && tagItem.id === item.id)
    if (isSelected) {
      setSelectedTags((current) => current.filter((tagItem) => tagItem.type !== item.type || tagItem.id !== item.id))
      return
    }
    if (selectedTags.length >= 5) {
      setToast('Posts can include up to 5 linked tags')
      return
    }
    setSelectedTags((current) => [...current, item])
  }

  function updateMentionContext(value: string, caret: number | null) {
    const nextContext = mentionContextAt(value, caret)
    if (nextContext?.query !== mentionContext?.query) setActiveMentionIndex(0)
    setMentionContext(nextContext)
  }

  function handleDraftChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const nextDraft = event.target.value
    setDraft(nextDraft)
    updateMentionContext(nextDraft, event.target.selectionStart)
  }

  function selectMention(item: CatalogOption) {
    if (!mentionContext) return
    const alreadyTagged = selectedTags.some((tagItem) => tagItem.type === item.type && tagItem.id === item.id)
    if (!alreadyTagged && selectedTags.length >= 5) {
      setToast('Posts can include up to 5 linked tags')
      return
    }

    const mentionText = `@${item.label}`
    const afterMention = draft.slice(mentionContext.end)
    const separator = !afterMention || !/^[\s,!?;:.)\]}]/.test(afterMention) ? ' ' : ''
    const nextDraft = `${draft.slice(0, mentionContext.start)}${mentionText}${separator}${afterMention}`
    const nextCaret = mentionContext.start + mentionText.length + separator.length
    setDraft(nextDraft)
    if (!alreadyTagged) setSelectedTags((current) => [...current, item])
    setMentionContext(null)
    setMentionOptions([])
    setActiveMentionIndex(0)

    window.requestAnimationFrame(() => {
      composerRef.current?.focus()
      composerRef.current?.setSelectionRange(nextCaret, nextCaret)
    })
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!mentionContext) return
    if (event.key === 'Escape') {
      event.preventDefault()
      setMentionContext(null)
      return
    }
    if (mentionOptions.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveMentionIndex((current) => (current + 1) % mentionOptions.length)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveMentionIndex((current) => (current - 1 + mentionOptions.length) % mentionOptions.length)
      return
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      selectMention(mentionOptions[activeMentionIndex] ?? mentionOptions[0])
    }
  }

  async function publishPost() {
    const cleanDraft = draft.trim()
    if (!cleanDraft || postBusy) return
    const member = requireAuth('composer')
    if (!member) return
    setPostBusy(true)
    try {
      const payload = await apiJson<{ post?: Record<string, unknown>; id?: string }>('/api/posts', {
        method: 'POST',
        body: JSON.stringify({
          body: cleanDraft,
          tags: selectedTags.map((item) => ({ type: item.type, id: item.id })),
          visibility: 'public',
          quotePostId: quotePost?.id,
        }),
      })
      const rawPost: Record<string, unknown> = payload.post ?? (payload as Record<string, unknown>)
      const author = (rawPost.author ?? {}) as Record<string, unknown>
      const name = String(author.display_name ?? member.name).replace(/ · Demo( fan)?$/, '')
      setPostItems((current) => [{
        id: String(rawPost.id ?? payload.id ?? `posted-${Date.now()}`),
        authorId: String(author.id ?? member.id),
        name,
        handle: author.handle ? `@${String(author.handle)}` : member.handle,
        time: 'now',
        avatar: initialsFor(name),
        avatarUrl: avatarUrlFromPayload(author) ?? member.avatarUrl,
        tone: 'lime',
        body: cleanDraft,
        tags: selectedTags.map((item) => item.label),
        tagEntities: selectedTags,
        quotePostId: typeof rawPost.quote_post_id === 'string' ? rawPost.quote_post_id : quotePost?.id ?? null,
        likes: 0,
        replies: 0,
        reposts: 0,
      }, ...current])
      setDraft('')
      setSelectedTags([])
      setMentionContext(null)
      setMentionOptions([])
      setQuotePost(null)
      setFeedRevision((current) => current + 1)
      setToast('Your post is live')
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Your post could not be published')
    } finally {
      setPostBusy(false)
    }
  }

  async function togglePost(postId: string, key: 'liked' | 'bookmarked') {
    const post = postItems.find((item) => item.id === postId)
    if (!post) return
    if (key === 'liked' && !requireAuth('like', () => { void togglePost(postId, key) })) return
    const nextValue = !post[key]
    setPostItems((current) => current.map((item) => item.id === postId
      ? { ...item, [key]: nextValue, likes: key === 'liked' ? item.likes + (nextValue ? 1 : -1) : item.likes }
      : item))

    if (key === 'bookmarked') {
      const saved = new Set(storedIds('journey90:bookmarks'))
      if (nextValue) saved.add(postId)
      else saved.delete(postId)
      window.localStorage.setItem('journey90:bookmarks', JSON.stringify([...saved]))
      setToast(nextValue ? 'Saved to bookmarks' : 'Removed from bookmarks')
      return
    }

    try {
      await apiJson('/api/reactions', {
        method: nextValue ? 'POST' : 'DELETE',
        body: JSON.stringify({ targetType: 'post', targetId: postId, reactionType: 'like' }),
      })
    } catch (error) {
      setPostItems((current) => current.map((item) => item.id === postId
        ? { ...item, liked: !nextValue, likes: item.likes + (nextValue ? -1 : 1) }
        : item))
      setToast(error instanceof Error ? error.message : 'Reaction could not be saved')
    }
  }

  async function sendLiveMessage() {
    const cleanMessage = liveMessage.trim()
    if (!cleanMessage || liveSending || !liveCanPost) return
    const member = requireAuth('live')
    if (!member) return
    const context = liveContextRef.current
    if (context.slug !== activeLiveShow.roomSlug || context.memberId !== member.id) return
    setLiveSending(true)
    try {
      const payload = await apiJson<{ message?: Record<string, unknown> }>('/api/live', {
        method: 'POST',
        body: JSON.stringify({ action: 'message', slug: context.slug, body: cleanMessage, clientRequestId: `live_${crypto.randomUUID()}` }),
      })
      if (!sameLiveRoomContext(context, liveContextRef.current)) return
      const row = payload.message ?? {}
      const author = (row.author ?? {}) as Record<string, unknown>
      const name = String(author.display_name ?? member.name).replace(/ · Demo( fan)?$/, '')
      setLiveMessages((current) => mergeLiveMessages(current, [{
        id: String(row.id ?? `message-${Date.now()}`),
        authorId: String(author.id ?? member.id),
        initials: initialsFor(name),
        avatarUrl: avatarUrlFromPayload(author) ?? member.avatarUrl,
        name,
        handle: author.handle ? `@${String(author.handle)}` : undefined,
        body: cleanMessage,
        tone: 'lime',
        sentAt: 'now',
        reactionCount: Number(row.reaction_count ?? 0),
        reacted: Boolean(row.viewer_reaction),
        reactable: true,
      }]))
      setLiveMessage('')
    } catch (error) {
      if (sameLiveRoomContext(context, liveContextRef.current)) setToast(error instanceof Error ? error.message : 'Message could not be sent')
    } finally {
      if (sameLiveRoomContext(context, liveContextRef.current)) setLiveSending(false)
    }
  }

  async function toggleLatestLiveReaction() {
    const target = [...liveMessages].reverse().find((message) => message.reactable)
    if (!target || liveReacting || !liveCanPost) return
    const member = requireAuth('live')
    if (!member) return
    const context = liveContextRef.current
    if (context.slug !== activeLiveShow.roomSlug || context.memberId !== member.id) return
    const nextActive = !target.reacted
    const previousCount = target.reactionCount ?? 0
    const optimisticCount = Math.max(0, previousCount + (nextActive ? 1 : -1))
    liveReactionVersionRef.current += 1
    livePendingReactionRef.current = { context, messageId: target.id, active: nextActive, count: optimisticCount }
    setLiveReacting(true)
    setLiveMessages((current) => current.map((message) => message.id === target.id
      ? { ...message, reacted: nextActive, reactionCount: optimisticCount }
      : message))

    try {
      const payload = await apiJson<{ message?: Record<string, unknown> }>('/api/live', {
        method: 'POST',
        body: JSON.stringify({
          action: 'reaction',
          slug: context.slug,
          messageId: target.id,
          reactionType: 'laugh',
          active: nextActive,
        }),
      })
      if (!sameLiveRoomContext(context, liveContextRef.current)) return
      const persistedCount = Number(payload.message?.reaction_count ?? optimisticCount)
      setLiveMessages((current) => current.map((message) => message.id === target.id
        ? { ...message, reacted: nextActive, reactionCount: persistedCount }
        : message))
      setToast(nextActive ? `Reacted to ${target.name}` : 'Reaction removed')
    } catch (error) {
      if (!sameLiveRoomContext(context, liveContextRef.current)) return
      setLiveMessages((current) => current.map((message) => message.id === target.id
        ? { ...message, reacted: target.reacted, reactionCount: previousCount }
        : message))
      setToast(error instanceof Error ? error.message : 'Reaction could not be saved')
    } finally {
      if (sameLiveRoomContext(context, liveContextRef.current)) {
        livePendingReactionRef.current = null
        liveReactionVersionRef.current += 1
        setLiveReacting(false)
      }
    }
  }

  function openLiveRooms() {
    setPanel('rooms')
    setAccountMenuOpen(false)
  }

  function openLiveRoom(show: RoomSelection) {
    setActiveLiveShow(show)
    setPanel('live')
  }

  async function toggleFollow(targetType: 'member' | 'person' | 'relationship', targetId: string, label: string) {
    if (!targetId) return
    if (!requireAuth('follow', () => { void toggleFollow(targetType, targetId, label) })) return
    followMutationVersionRef.current += 1
    const key = `${targetType}:${targetId}`
    const nextValue = !followedTargets.has(key)
    setFollowedTargets((current) => {
      const next = new Set(current)
      if (nextValue) next.add(key)
      else next.delete(key)
      return next
    })
    try {
      await apiJson('/api/follows', {
        method: nextValue ? 'POST' : 'DELETE',
        body: JSON.stringify({ targetType, targetId }),
      })
      setFeedRevision((current) => current + 1)
      setToast(nextValue ? `Following ${label}` : `Unfollowed ${label}`)
    } catch (error) {
      setFollowedTargets((current) => {
        const reverted = new Set(current)
        if (nextValue) reverted.delete(key)
        else reverted.add(key)
        return reverted
      })
      setToast(error instanceof Error ? error.message : 'Follow could not be saved')
    }
  }

  function openComments(post: Post) {
    setHighlightedCommentId(null)
    if (activePost?.id !== post.id) setCommentDraft('')
    setActivePost(post)
    setPanel('comments')
  }

  function startCommentReply(comment: Comment) {
    if (!activePost || commentBusy || commentsLoading) return
    if (!requireAuth('reply', () => startCommentReply(comment))) return
    const member = currentMemberRef.current
    if (!member || !comments.some(row => row.id === comment.id)) return
    setCommentReplyTarget({ id: comment.id, postId: activePost.id, memberId: member.id, authorName: comment.author.name, body: comment.body })
    setConfirmCommentDelete(null)
    window.requestAnimationFrame(() => commentReplyRef.current?.focus())
  }

  function cancelCommentReply() {
    setCommentReplyTarget(null)
    window.requestAnimationFrame(() => commentReplyRef.current?.focus())
  }

  async function openNotificationConversation(notification: ReplyNotification, signal: AbortSignal) {
    const memberId = currentMemberRef.current?.id
    if (!memberId) throw new Error('Sign in to view this notification.')
    const version = ++notificationOpenVersionRef.current
    const [postPayload, repliesPayload] = await Promise.all([
      apiJson<{post: Record<string, unknown>}>(`/api/posts/${encodeURIComponent(notification.post_id)}`, {signal}),
      apiJson<{viewer_member_id?: string | null; selected_comment?: Record<string, unknown> | null}>(`/api/posts/${encodeURIComponent(notification.post_id)}/comments?target_comment_id=${encodeURIComponent(notification.comment_id)}`, {signal}),
    ])
    if (signal.aborted || version !== notificationOpenVersionRef.current || currentMemberRef.current?.id !== memberId) throw new Error('Your account changed. Open notifications again.')
    if (repliesPayload.viewer_member_id !== memberId) throw new Error('Your account changed. Refresh Journey90 to continue.')
    if (postPayload.post?.id !== notification.post_id || repliesPayload.selected_comment?.id !== notification.comment_id) throw new Error('That reply is no longer available. Refresh your notifications.')
    setMobileSection('feed')
    setAccountMenuOpen(false)
    setOptionsPostId(null)
    if (activePost?.id !== notification.post_id) setCommentDraft('')
    scrolledNotificationCommentRef.current = null
    setHighlightedCommentId(notification.comment_id)
    setActivePost(postFromPayload(postPayload.post))
    setPanel('comments')
  }

  async function publishComment() {
    const body = commentDraft.trim()
    if (!activePost || !body || commentBusy) return
    const member = requireAuth('reply')
    if (!member) return
    const context = commentsContextRef.current
    const postId = activePost.id
    if (commentReplyTarget && !comments.some(row => row.id === commentReplyTarget.id)) {
      setToast('That comment is no longer available. Cancel the direct reply or choose another comment. Your draft has been kept.')
      return
    }
    setCommentBusy(true)
    try {
      const input = commentReplyInput(body, commentReplyTarget, postId, member.id)
      const payload = await apiJson<{ comment?: Record<string, unknown> }>(`/api/posts/${encodeURIComponent(activePost.id)}/comments`, {
        method: 'POST',
        headers: { 'x-journey90-viewer-id': member.id },
        body: JSON.stringify(input),
      })
      const row = payload.comment ?? {}
      const author = (row.author ?? {}) as Record<string, unknown>
      const name = String(author.display_name ?? member.name).replace(/ · Demo( fan)?$/, '')
      if (commentsContextRef.current === context && context.memberId === member.id) setComments((current) => [...current, {
        id: String(row.id ?? `comment-${Date.now()}`),
        parentCommentId: typeof row.parent_comment_id === 'string' ? row.parent_comment_id : null,
        body,
        postedAt: 'now',
        author: { id: String(author.id ?? member.id), name, handle: author.handle ? `@${String(author.handle)}` : member.handle, initials: initialsFor(name), avatarUrl: avatarUrlFromPayload(author) ?? member.avatarUrl },
      }])
      setPostItems((current) => current.map((item) => item.id === postId ? { ...item, replies: item.replies + 1 } : item))
      if (commentsContextRef.current === context) { setCommentDraft(''); setCommentReplyTarget(null) }
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Reply could not be published')
    } finally {
      setCommentBusy(false)
    }
  }

  async function deleteOwnComment(comment: Comment) {
    if (!currentMember || comment.author.id !== currentMember.id || !activePost || commentBusy) return
    if (confirmCommentDelete !== comment.id) { setConfirmCommentDelete(comment.id); return }
    setCommentBusy(true)
    try {
      await apiJson(`/api/comments/${encodeURIComponent(comment.id)}`, {method:'DELETE'})
      setComments((current) => current.filter((item) => item.id !== comment.id))
      setConfirmCommentDelete(null)
      setPostItems((current) => current.map((item) => item.id === activePost.id ? {...item,replies:Math.max(0,item.replies-1)} : item))
      setToast('Your reply was removed')
    } catch (error) { setToast(error instanceof Error ? error.message : 'Reply could not be removed') }
    finally { setCommentBusy(false) }
  }

  function openPostOptions(post: Post) {
    if (post.authorId !== currentMember?.id) return
    setOptionsPostId(post.id)
    setEditPostBody(post.body)
    setConfirmPostDelete(false)
  }

  async function saveOwnPost() {
    const post = postItems.find((item) => item.id === optionsPostId)
    if (!post || post.authorId !== currentMember?.id || !editPostBody.trim() || postActionBusy) return
    setPostActionBusy(true)
    try {
      const {post: updated} = await apiJson<{post: Record<string, unknown>}>(`/api/posts/${encodeURIComponent(post.id)}`, {method:'PATCH',body:JSON.stringify({body:editPostBody.trim()})})
      setPostItems((current) => current.map((item) => item.id === post.id ? postFromPayload(updated) : item))
      setOptionsPostId(null)
      setToast('Your post was updated')
    } catch (error) { setToast(error instanceof Error ? error.message : 'Post could not be updated') }
    finally { setPostActionBusy(false) }
  }

  async function deleteOwnPost() {
    const post = postItems.find((item) => item.id === optionsPostId)
    if (!post || post.authorId !== currentMember?.id || postActionBusy) return
    if (!confirmPostDelete) { setConfirmPostDelete(true); return }
    setPostActionBusy(true)
    try {
      await apiJson(`/api/posts/${encodeURIComponent(post.id)}`, {method:'DELETE'})
      setPostItems((current) => current.filter((item) => item.id !== post.id))
      const bookmarks = storedIds('journey90:bookmarks').filter((id) => id !== post.id)
      try { window.localStorage.setItem('journey90:bookmarks', JSON.stringify(bookmarks)) } catch { /* Server deletion already succeeded; browser storage is best-effort. */ }
      setOptionsPostId(null)
      setFeedRevision((current) => current + 1)
      setToast('Your post was removed')
    } catch (error) { setToast(error instanceof Error ? error.message : 'Post could not be removed') }
    finally { setPostActionBusy(false) }
  }

  function openModeration(target: ModerationTarget | null) {
    if (currentMemberRef.current?.role !== 'admin') return
    setAccountMenuOpen(false)
    setModerationTarget(target)
    setModerationOpen(true)
  }

  function applyModeration(result: ModerationCompletion) {
    if (currentMemberRef.current?.role !== 'admin') return
    setQuotedPosts({})
    if (result.action === 'remove_post') {
      setPostItems((current) => current.filter((post) => post.id !== result.target_id))
      setQuotePost((current) => current?.id === result.target_id ? null : current)
      if (activePost?.id === result.target_id) { setActivePost(null); setPanel(null); setComments([]) }
    } else if (result.action === 'remove_comment') {
      setComments((current) => current.filter((comment) => comment.id !== result.target_id))
    } else if (result.action === 'remove_live_message') {
      setLiveMessages((current) => current.filter((message) => message.id !== result.target_id))
    } else if (result.action === 'block_member') {
      setPostItems((current) => current.filter((post) => post.authorId !== result.target_id))
      setComments((current) => current.filter((comment) => comment.author.id !== result.target_id))
      setLiveMessages((current) => current.filter((message) => message.authorId !== result.target_id))
      setQuotePost((current) => current?.authorId === result.target_id ? null : current)
      if (activePost?.authorId === result.target_id) { setActivePost(null); setPanel(null); setComments([]) }
    }
    setFeedRevision((current) => current + 1)
    setModerationRevision((current) => current + 1)
    setLiveRefreshRevision((current) => current + 1)
    setToast(result.status === 'blocked' ? 'Member blocked site-wide' : result.status === 'active' ? 'Member unblocked' : 'Content removed')
  }

  function quotePostInComposer(post: Post) {
    setQuotePost(post)
    setDraft('')
    setPanel(null)
    window.requestAnimationFrame(openComposer)
  }

  async function sharePost(post: Post) {
    const url = `${window.location.origin}${window.location.pathname}#post-${post.id}`
    try {
      await navigator.clipboard.writeText(url)
      setToast('Post link copied')
    } catch {
      setToast('Copy this page URL to share the post')
    }
  }

  function showMobileSection(section: 'feed' | 'cast' | 'schedule') {
    setPanel(null)
    setAccountMenuOpen(false)
    setMobileSection(section)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  function openSpotlightPerson(personId: string) {
    const entity = castCatalogOptions.find((item) => item.id === personId)
    if (entity) openEntity(entity)
  }

  function handleNav(label: string) {
    if (label === 'Live rooms') openLiveRooms()
    else if (label === 'Following') {
      if (requireAuth('following')) setFeed('Following')
    }
    else if (label === 'Library') {
      setCatalogQuery('')
      setCatalogFilter('people')
      setPanel('library')
    }
    else if (['Couples', 'Cast', 'Episodes'].includes(label)) {
      setCatalogQuery('')
      setCatalogFilter(label === 'Couples' ? 'relationships' : label === 'Cast' ? 'people' : 'episodes')
      setPanel('library')
    } else if (label === 'Bookmarks') {
      setPostItems((current) => [...current].sort((a, b) => Number(Boolean(b.bookmarked)) - Number(Boolean(a.bookmarked))))
      setToast('Bookmarked posts moved to the top')
    } else if (label === 'Groups') setToast(comingSoonFeatures.groups.notice)
  }

  const mobileActiveSection = accountMenuOpen ? 'account' : panel === 'rooms' || panel === 'live' ? 'live' : mobileSection

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Journey90 home" onClick={() => showMobileSection('feed')}>
          <span className="brand-mark" aria-hidden="true">
            <span className="brand-mark__monogram"><span>J</span><b>90</b></span>
          </span>
          <span className="brand-copy">
            <span className="brand-name">journey<span>90</span></span>
            <span className="brand-tagline">The Ultimate 90 Day Fiancé Fan site</span>
          </span>
        </a>

        <label className="global-search">
          <Search size={18} aria-hidden="true" />
          <input
            aria-label="Search Journey90"
            placeholder="Search cast, couples or episodes"
            value={globalQuery}
            onChange={(event) => setGlobalQuery(event.target.value)}
          />
          {globalQuery.trim() && (
            <div className="search-popover">
              <span className="eyebrow">LIBRARY RESULTS</span>
              {globalSearchLoading && <p className="search-state" role="status">Searching the complete library…</p>}
              {!globalSearchLoading && globalMatches.length === 0 && <p className="search-state" role="status">{globalSearchError ? 'Search is temporarily unavailable. Try the full index below.' : 'No matching library records.'}</p>}
              {globalMatches.map((item) => (
                <button key={item.id} type="button" onClick={() => openEntity(item)}>
                  <span className={`result-icon result-icon--${item.type}`}>{item.initials}</span>
                  <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                  <ChevronRight size={16} />
                </button>
              ))}
              <button type="button" className="search-all" onClick={() => { setCatalogQuery(globalQuery); setCatalogFilter('people'); setGlobalQuery(''); setPanel('library') }}>Search the complete index <ExternalLink size={14} /></button>
            </div>
          )}
        </label>

        <div className="topbar-actions">
          <button className="mobile-post-button" type="button" aria-label="Create a post" onClick={openComposer}><Plus size={22} /></button>
          {sessionStatus === 'checking' && <span className="session-checking" aria-label="Checking sign in status" />}
          {sessionStatus !== 'checking' && !currentMember && (
            <>
              <button className="auth-login-button" type="button" onClick={() => beginAuth()}><LogIn size={17} /> Log in</button>
              <button className="auth-join-button" type="button" onClick={() => beginAuth()}><UserPlus size={17} /> Join Journey90</button>
            </>
          )}
          {currentMember && (
            <>
              <ReplyNotifications key={currentMember.id} memberId={currentMember.id} revision={moderationRevision} onOpenConversation={openNotificationConversation} onError={setToast} />
              <div className="account-menu-wrap">
                <button className="avatar-button" type="button" aria-haspopup="menu" aria-expanded={accountMenuOpen} aria-label={`Open ${currentMember.name} account menu`} onClick={() => setAccountMenuOpen((current) => !current)}><Avatar initials={currentMember.initials} imageUrl={currentMember.avatarUrl} tone="lime" small /></button>
                {accountMenuOpen && (
                  <div className="account-menu" role="menu" aria-label={`${currentMember.name} account`}>
                    <div><Avatar initials={currentMember.initials} imageUrl={currentMember.avatarUrl} tone="lime" small /><span><strong>{currentMember.name}</strong><small>{currentMember.handle}</small></span></div>
                    {currentMember.role === 'admin' && <em>Journey90 host</em>}
                    <button type="button" role="menuitem" aria-haspopup="dialog" onClick={() => { setAccountMenuOpen(false); setEditProfileOpen(true) }}><User size={15} /> Edit profile</button>
                    <button type="button" role="menuitem" onClick={() => { showMobileSection('feed'); handleNav('Bookmarks') }}><Bookmark size={15} /> Bookmarks</button>
                    {currentMember.role === 'admin' && <button type="button" role="menuitem" aria-haspopup="dialog" onClick={() => openModeration(null)}><Shield size={15} /> Admin moderation</button>}
                    <button type="button" role="menuitem" onClick={() => void signOut()}><LogOut size={15} /> Log out</button>
                  </div>
                )}
              </div>
              <button className="primary-button" onClick={openComposer}><Plus size={18} /> Post</button>
            </>
          )}
        </div>
      </header>

      <main className="page-grid" id="top" data-mobile-section={mobileSection}>
        <aside className="left-rail">
          <nav className="primary-nav" aria-label="Primary navigation">
            {navItems.map(({ label, icon: Icon, active, comingSoon }) => (
              <button key={label} className={`nav-item ${active ? 'is-active' : ''}`} type="button" onClick={() => handleNav(label)} disabled={comingSoon} aria-label={comingSoon ? comingSoonFeatures.groups.accessibleLabel : undefined} title={comingSoon ? comingSoonFeatures.groups.notice : undefined}>
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                <span>{label}</span>
                {comingSoon && <span className="nav-badge">Coming soon</span>}
              </button>
            ))}
          </nav>

          <section className="library-card">
            <span className="eyebrow">THE LIBRARY</span>
            <h3>Every journey, connected.</h3>
            <div className="library-stats">
              <div><strong>{catalogTotals?.cast ?? '—'}</strong><span>cast</span></div>
              <div><strong>{catalogTotals?.relationships ?? '—'}</strong><span>couples</span></div>
              <div><strong>{catalogTotals?.episodes ?? '—'}</strong><span>episodes</span></div>
            </div>
            <button className="text-button" onClick={() => handleNav('Library')}>Browse the full index <ChevronRight size={16} /></button>
          </section>

          <p className="rail-note">Fan-built and independent. Not affiliated with TLC or Warner Bros. Discovery.</p>
        </aside>

        <section className="feed-column" aria-label="Community feed">
          <div className="feed-heading">
            <div>
              <h1>What’s everyone saying?</h1>
            </div>
            <button className="feed-control" type="button" aria-label="Refresh newest posts" title="Reload the latest posts in this feed" disabled={feedLoading} onClick={() => setFeedRevision((current) => current + 1)}><span>Newest</span><RefreshCw size={15} /></button>
          </div>

          <div className="feed-tabs" role="tablist">
            {(['For you', 'Following'] as const).map((tab) => (
              <button key={tab} role="tab" aria-selected={feed === tab} onClick={() => {
                if (tab === 'Following' && !requireAuth('following')) return
                setFeed(tab)
              }} className={feed === tab ? 'is-active' : ''}>{tab}</button>
            ))}
          </div>

          <section className="premiere-countdowns" aria-label="Next episode countdowns">
            {launchShows.map((show) => <Countdown key={show.id} show={show} onOpenRoom={openLiveRoom} />)}
          </section>

          <CastSpotlight portraits={spotlightPortraits} onViewAll={() => handleNav('Cast')} onOpenPerson={openSpotlightPerson} />

          <section className={`composer-card${currentMember ? '' : ' signed-out-composer'}`} onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setMentionContext(null)
          }}>
            {quotePost && (
              <div className="quote-context">
                <Repeat2 size={15} />
                <span><strong>Share with your take</strong><small>{quotePost.name}: {quotePost.body}</small></span>
                <button type="button" onClick={() => setQuotePost(null)} aria-label="Remove quoted post"><X size={15} /></button>
              </div>
            )}
            <div className="composer-main">
              <Avatar initials={currentMember?.initials ?? 'J9'} imageUrl={currentMember?.avatarUrl} tone="lime" />
              <div className="composer-input">
                <textarea
                  ref={composerRef}
                  role="combobox"
                  aria-label="Create a post"
                  aria-autocomplete="list"
                  aria-haspopup="listbox"
                  aria-expanded={mentionContext !== null}
                  aria-controls={mentionContext ? 'composer-mention-listbox' : undefined}
                  aria-activedescendant={mentionContext && mentionOptions.length > 0 ? `composer-mention-${activeMentionIndex}` : undefined}
                  placeholder="Share a take, start a rewatch, or type @ to tag…"
                  value={draft}
                  onChange={handleDraftChange}
                  onKeyDown={handleComposerKeyDown}
                  onSelect={(event) => updateMentionContext(event.currentTarget.value, event.currentTarget.selectionStart)}
                />
                {mentionContext && (
                  <div className="mention-popover" id="composer-mention-listbox" role="listbox" aria-label="Tag a cast member or couple">
                    <header>
                      <span><AtSign size={14} /> Tag a Cast Member</span>
                      <small>{mentionLoading ? 'Searching…' : '↑↓ browse · Enter to tag'}</small>
                    </header>
                    <div className="mention-results" aria-live="polite">
                      {mentionLoading && mentionOptions.length === 0 && <div className="mention-state"><i /><span>Searching the Journey90 index…</span></div>}
                      {!mentionLoading && mentionOptions.length === 0 && <div className="mention-state"><AtSign size={17} /><span>No cast member or couple matches @{mentionContext.query}</span></div>}
                      {mentionOptions.map((item, index) => {
                        const isTagged = selectedTags.some((tagItem) => tagItem.type === item.type && tagItem.id === item.id)
                        return (
                          <button
                            id={`composer-mention-${index}`}
                            key={`${item.type}-${item.id}`}
                            type="button"
                            role="option"
                            aria-selected={index === activeMentionIndex}
                            className={index === activeMentionIndex ? 'is-active' : ''}
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActiveMentionIndex(index)}
                            onClick={() => selectMention(item)}
                          >
                            <span className={`result-icon result-icon--${item.type}`}>{item.initials}</span>
                            <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                            <span className={`mention-action ${isTagged ? 'is-tagged' : ''}`}>{isTagged ? <Check size={14} /> : <Plus size={14} />}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!currentMember && sessionStatus !== 'checking' && (
              <div className="composer-auth-note"><LockKeyhole size={14} /><span>Join to publish, follow couples, and chat live.</span><button type="button" onClick={() => beginAuth('composer')}>Join or log in</button></div>
            )}
            {selectedTags.length > 0 && (
              <div className="selected-tags">
                {selectedTags.map((item) => <button type="button" key={`${item.type}-${item.id}`} onClick={() => toggleTag(item)}><Tag size={13} />{item.label}<X size={12} /></button>)}
              </div>
            )}
            <div className="composer-footer">
              <div className="composer-tools">
                <button type="button" aria-label="Tag a Cast Member" onClick={() => { setCatalogQuery(''); setCatalogFilter('people'); setPanel('tags') }}><Tag size={17} /><span>Tag a Cast Member</span></button>
                <button type="button" aria-label={comingSoonFeatures.media.accessibleLabel} title={comingSoonFeatures.media.notice} disabled><Image size={17} /><span>Media · Coming soon</span></button>
                <button type="button" aria-label="Tag an episode" onClick={() => { setCatalogQuery(''); setCatalogFilter('episodes'); setPanel('tags') }}><CalendarDays size={17} /><span>Episode</span></button>
              </div>
              <button className="post-button" disabled={!draft.trim() || postBusy} onClick={publishPost}>{postBusy ? 'Posting…' : !currentMember ? 'Sign in to post' : quotePost ? 'Share' : 'Post'} <Send size={16} /></button>
            </div>
          </section>

          <div className="launch-cast-mobile">
            <LaunchCastHub shows={launchShows} onOpenEntity={openEntity} onOpenRoom={openLiveRoom} />
          </div>

          <div className="context-strip">
            <span><BookOpen size={16} /> Feed context</span>
            <p>Tap any pink tag to open its verified library page without leaving the conversation.</p>
          </div>

          <div className="posts-list">
            {sharedPostError && <p className="feed-state" role="status">{sharedPostError}</p>}
            {feedError && <p className="feed-state" role="alert">{feedError}</p>}
            {feedLoading && <div className="feed-state"><span className="feed-spinner" />Loading the conversation…</div>}
            {!feedLoading && !feedError && postItems.length === 0 && (
              <div className="feed-empty">
                <UserPlus size={24} />
                <strong>{feed === 'Following' ? 'Your following feed is ready for people you choose.' : 'The conversation is ready for you.'}</strong>
                <span>{feed === 'Following' ? 'Follow a fan, cast member, or couple and their posts will collect here.' : 'No posts are available right now. Share a take or refresh the feed.'}</span>
                {feed === 'Following' && <button type="button" onClick={() => setFeed('For you')}>Find people to follow</button>}
              </div>
            )}
            {!feedLoading && postItems.map((post) => {
              const isFollowingAuthor = followedTargets.has(`member:${post.authorId}`)
              return (
              <article className="post-card" id={`post-${post.id}`} key={post.id} tabIndex={-1}>
                <Avatar initials={post.avatar} imageUrl={post.avatarUrl} tone={post.tone} />
                <div className="post-content">
                  <header className="post-header">
                    <div><strong>{post.name}</strong><span>{post.handle} · {post.time}</span></div>
                    <div className="post-header__actions">
                      {post.authorId !== currentMember?.id && <button className={`post-follow ${isFollowingAuthor ? 'is-following' : ''}`} type="button" onClick={() => toggleFollow('member', post.authorId, post.name)}>
                        {isFollowingAuthor ? <UserCheck size={14} /> : <UserPlus size={14} />}{isFollowingAuthor ? 'Following' : 'Follow'}
                      </button>}
                      {post.authorId === currentMember?.id && <button className="quiet-button" aria-label="More options" onClick={() => openPostOptions(post)}><MoreHorizontal size={20} /></button>}
                      {currentMember?.role === 'admin' && post.authorId !== currentMember.id && <button type="button" className="quiet-button admin-content-control" aria-label={`Moderate post by ${post.name}`} title="Remove post or block member" onClick={() => openModeration({kind:'post',id:post.id,authorId:post.authorId,authorName:post.name,body:post.body})}><MoreHorizontal size={20} /></button>}
                    </div>
                  </header>
                  <p className="post-copy">{post.body}</p>
                  {post.quotePostId && (
                    <div className="quoted-post">
                      {quotedPosts[post.quotePostId] ? <>
                        <strong>{quotedPosts[post.quotePostId]!.name} <small>{quotedPosts[post.quotePostId]!.handle}</small></strong>
                        <p>{quotedPosts[post.quotePostId]!.body}</p>
                        <a href={`#post-${post.quotePostId}`}>Open original post <ChevronRight size={14} /></a>
                      </> : <span>{post.quotePostId in quotedPosts ? 'Original post is unavailable or requires access.' : 'Loading original post…'}</span>}
                    </div>
                  )}
                  <div className="entity-tags">
                    {post.tags.map((tag, tagIndex) => {
                      const canonicalTag = post.tagEntities?.[tagIndex]
                        ?? featuredCatalog.find((item) => item.label === tag)
                        ?? {
                          id: tag,
                          type: tagIndex === 0 ? 'relationship' : 'episode',
                          label: tag,
                          detail: 'Connected franchise library record',
                          initials: tag.split(' ').slice(0, 2).map((part) => part[0]).join(''),
                        } satisfies CatalogOption
                      return (
                        <button
                          key={`${canonicalTag.type}:${canonicalTag.id}`}
                          className={canonicalTag.type === 'relationship' ? 'couple-tag' : ''}
                          onClick={() => openEntity(canonicalTag)}
                        >
                          {canonicalTag.type === 'relationship' ? <Heart size={14} /> : canonicalTag.type === 'episode' ? <Library size={14} /> : <Hash size={14} />}
                          {canonicalTag.label}
                        </button>
                      )
                    })}
                  </div>
                  {post.tags.includes('Gino & Jasmine') && (
                    <button className="journey-peek" onClick={() => openEntity(featuredCatalog[0])}>
                      <div className="pair-avatar"><span>G</span><span>J</span></div>
                      <div><span className="eyebrow">COUPLE JOURNEY</span><strong>Gino & Jasmine</strong><small>Open their connected relationship</small></div>
                      <span className="journey-arrow"><ChevronRight size={19} /></span>
                    </button>
                  )}
                  <footer className="post-actions">
                    <button onClick={() => openComments(post)}><MessageCircle size={18} /><span>{post.replies}</span></button>
                    <button onClick={() => quotePostInComposer(post)}><Repeat2 size={18} /><span>{post.reposts}</span></button>
                    <button className={post.liked ? 'is-active' : ''} onClick={() => togglePost(post.id, 'liked')}><Heart size={18} fill={post.liked ? 'currentColor' : 'none'} /><span>{post.likes}</span></button>
                    <button aria-label="Share" onClick={() => sharePost(post)}><Share2 size={18} /></button>
                    <button className={post.bookmarked ? 'is-active' : ''} aria-label="Bookmark" onClick={() => togglePost(post.id, 'bookmarked')}><Bookmark size={18} fill={post.bookmarked ? 'currentColor' : 'none'} /></button>
                  </footer>
                </div>
              </article>
            )})}
          </div>
        </section>

        <aside className="right-rail">
          <section className="next-room-card">
            <header><span className="live-pill"><i /> NEXT EPISODES</span><small>Rooms open early</small></header>
            {launchShows.map((show) => <Countdown compact key={show.id} show={show} onOpenRoom={openLiveRoom} />)}
          </section>

          <div className="right-cast-hub">
            <LaunchCastHub shows={launchShows} onOpenEntity={openEntity} onOpenRoom={openLiveRoom} />
          </div>

          <section className="side-card trending-card">
            <header><h3>Conversation starters</h3><button type="button" aria-label="Browse all cast, couples and episodes" onClick={() => handleNav('Library')}>View all</button></header>
            {[
              ['01', 'The Other Way · S8', 'Premieres Sunday'],
              ['02', 'Hunt for Love · S2', 'Premieres Monday'],
              ['03', 'Kenny & Armando', 'Returning couple'],
            ].map(([rank, topic, volume], index) => (
              <button className="trend-row" key={topic} onClick={() => {
                if (index < 2) {
                  openLiveRoom(launchShows[index])
                  return
                }
                const entity = featuredCatalog.find((item) => item.label === topic)
                if (entity) openEntity(entity)
                else {
                  setCatalogQuery(topic)
                  setCatalogFilter('relationships')
                  setPanel('library')
                }
              }}>
                <span>{rank}</span><div><strong>{topic}</strong><small>{volume}</small></div><ChevronRight size={16} />
              </button>
            ))}
          </section>

          <section className="side-card prompt-card">
            <span>QUESTION OF THE DAY</span>
            <h3>Which season deserves a community rewatch next?</h3>
            <button onClick={() => { setDraft('Which season deserves a community rewatch next? My pick is…'); openComposer() }}>Answer in the feed</button>
          </section>
        </aside>

        {mobileSection !== 'feed' && (
          <section className="mobile-destination" aria-label={mobileSection === 'cast' ? 'Cast discovery' : 'Next episodes'}>
            <header className="mobile-destination__heading">
              <h1>{mobileSection === 'cast' ? 'Cast' : 'Next episodes'}</h1>
              <p>{mobileSection === 'cast' ? 'Find your favorites and meet the newest cast.' : 'Showtimes in your local timezone. Join a room when you’re ready.'}</p>
              <button type="button" onClick={() => handleNav(mobileSection === 'cast' ? 'Cast' : 'Episodes')}><Library size={16} />{mobileSection === 'cast' ? 'Browse all cast, couples & episodes' : 'Browse the episode index'}<ChevronRight size={15} /></button>
            </header>
            {mobileSection === 'cast' ? (
              <>
                <CastSpotlight portraits={spotlightPortraits} onViewAll={() => handleNav('Cast')} onOpenPerson={openSpotlightPerson} />
                <LaunchCastHub shows={launchShows} onOpenEntity={openEntity} onOpenRoom={openLiveRoom} />
              </>
            ) : (
              <div className="mobile-destination__countdowns">
                {launchShows.map((show) => <Countdown key={show.id} show={show} onOpenRoom={openLiveRoom} />)}
              </div>
            )}
          </section>
        )}
      </main>

      <nav className="mobile-nav" aria-label="Mobile navigation">
        <button type="button" className={mobileActiveSection === 'feed' ? 'is-active' : ''} aria-current={mobileActiveSection === 'feed' ? 'page' : undefined} onClick={() => showMobileSection('feed')}><MessageCircle size={21} /><span>Feed</span></button>
        <button type="button" className={mobileActiveSection === 'cast' ? 'is-active' : ''} aria-current={mobileActiveSection === 'cast' ? 'page' : undefined} onClick={() => showMobileSection('cast')}><User size={21} /><span>Cast</span></button>
        <button type="button" className={mobileActiveSection === 'schedule' ? 'is-active' : ''} aria-current={mobileActiveSection === 'schedule' ? 'page' : undefined} onClick={() => showMobileSection('schedule')}><CalendarDays size={21} /><span>Next episodes</span></button>
        <button type="button" className={mobileActiveSection === 'live' ? 'is-active' : ''} aria-current={mobileActiveSection === 'live' ? 'page' : undefined} onClick={openLiveRooms}><Radio size={21} /><span>Live</span></button>
        <button type="button" className={mobileActiveSection === 'account' ? 'is-active' : ''} aria-haspopup={currentMember ? 'menu' : 'dialog'} aria-expanded={currentMember ? accountMenuOpen : undefined} onClick={() => currentMember ? setAccountMenuOpen((current) => !current) : beginAuth()}>{currentMember ? <User size={21} /> : <UserPlus size={21} />}<span>{currentMember ? 'Me' : 'Join'}</span></button>
      </nav>

      {panel && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPanel(null)
        }} onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation()
            setPanel(null)
          }
        }}>
          <section className={`modal-card modal-card--${panel}${panel === 'entity' && activeEntity?.type === 'person' ? ' modal-card--profile' : ''}`} role="dialog" aria-modal="true" aria-label={panel === 'rooms' ? 'Live rooms' : panel === 'live' ? 'Live episode room' : panel === 'comments' ? 'Post replies' : 'Franchise library'}>
            <button className="modal-close" aria-label="Close" onClick={() => setPanel(null)}><X size={20} /></button>

            {panel === 'rooms' && <LiveRoomDirectory viewerKey={currentMember?.id ?? 'guest'} onOpenRoom={openLiveRoom} />}

            {(panel === 'tags' || panel === 'library') && (
              <>
                <div className="modal-heading">
                  <span className="eyebrow">{panel === 'tags' ? 'CONNECT YOUR POST' : 'THE COMPLETE INDEX'}</span>
                  <h2>{panel === 'tags' ? 'Tag the conversation' : 'Every journey, connected'}</h2>
                  <p>{panel === 'tags'
                    ? 'Attach a cast member, couple, or episode so fans can jump straight to its page.'
                    : catalogTotals ? `Search across ${catalogTotals.cast} cast members, ${catalogTotals.relationships} couple journeys, and ${catalogTotals.episodes} canon episodes.` : 'Connecting to the live cast, couple and episode index…'}</p>
                </div>
                <label className="modal-search"><Search size={18} /><input
                  autoFocus={catalogFilter !== 'episodes'}
                  aria-label={catalogFilter === 'episodes' && selectedEpisodeSeries ? `Search ${catalogShowLabel(selectedEpisodeSeries)} episodes` : 'Search the franchise library'}
                  placeholder={catalogFilter === 'episodes' ? selectedEpisodeSeries ? `Search ${catalogShowLabel(selectedEpisodeSeries)} episodes` : 'Choose a show below to search episodes' : 'Search the franchise library'}
                  disabled={catalogFilter === 'episodes' && !selectedEpisodeSeries}
                  value={catalogQuery}
                  onChange={(event) => { invalidateCatalogRequests(); setCatalogQuery(event.target.value) }}
                /></label>
                <div className="catalog-filters">
                  {([
                    ['Couples', 'relationships'],
                    ['Cast', 'people'],
                    ['Episodes', 'episodes'],
                  ] as const).map(([label, value]) => <button type="button" aria-pressed={catalogFilter === value} className={catalogFilter === value ? 'is-active' : ''} key={value} onClick={() => changeCatalogFilter(value)}>{label}</button>)}
                </div>
                <div className="catalog-results" key={`${catalogFilter}:${selectedEpisodeSeries}:${catalogQuery}`} onScroll={(event) => {
                  const list = event.currentTarget
                  if (!catalogError && list.scrollHeight - list.scrollTop - list.clientHeight < 120) void loadMoreCatalog()
                }}>
                  {catalogFilter === 'episodes' && <EpisodeShowPicker
                    shows={catalogShows}
                    selectedSeries={selectedEpisodeSeries}
                    loading={catalogShowsLoading}
                    error={catalogShowsError}
                    onChoose={chooseEpisodeSeries}
                    selectedButtonRef={selectedShowButtonRef}
                    onRetry={() => setCatalogShowsRevision(revision => revision + 1)}
                  />}
                  {catalogFilter === 'episodes' && selectedEpisodeSeries && (
                    <label className="episode-index-sort">Season order
                      <select value={episodeSeasonOrder} onChange={(event) => { invalidateCatalogRequests(); setEpisodeSeasonOrder(event.target.value as EpisodeSeasonOrder) }}>
                        <option value="newest">Newest seasons first</option>
                        <option value="oldest">Oldest seasons first</option>
                      </select>
                    </label>
                  )}
                  {catalogLoading && <div className="catalog-loading"><i /><i /><i /></div>}
                  {catalogFilter === 'episodes' ? episodeGroups.map((show) => (
                    <section className="episode-index-show" key={show.series} aria-label={show.series}>
                      <h3>{show.series}</h3>
                      {show.seasons.map((season) => (
                        <section className="episode-index-season" key={season.season} aria-label={`${show.series} ${season.season === 0 ? 'Specials' : `Season ${season.season}`}`}>
                          <h4>{season.season === 0 ? 'Specials' : `Season ${season.season}`}</h4>
                          <div className="episode-season-list">{season.episodes.map(catalogResultButton)}</div>
                        </section>
                      ))}
                    </section>
                  )) : catalogMatches.map(catalogResultButton)}
                  {catalogError && <div className="catalog-error" role="alert"><p>{catalogError}</p><button type="button" onClick={() => catalogHasMore ? void loadMoreCatalog() : setCatalogRetryRevision(revision => revision + 1)}>Retry {catalogHasMore ? 'more results' : 'index'}</button></div>}
                  {catalogLoadingMore && <div className="catalog-loading" aria-label="Loading more results"><i /><i /><i /></div>}
                  {catalogHasMore && !catalogLoadingMore && !catalogError && <button type="button" className="catalog-load-more" onClick={() => void loadMoreCatalog()}>Load more {catalogFilter === 'episodes' ? 'episodes' : 'results'}</button>}
                  {!catalogLoading && !catalogError && catalogMatches.length === 0 && (catalogFilter !== 'episodes' || selectedEpisodeSeries) && <p className="empty-result">{catalogFilter === 'episodes' ? 'No matching episodes in this show. Try another search or choose another show.' : 'No matching results. Try another name.'}</p>}
                </div>
                {panel === 'tags' && <footer className="modal-footer"><span>{selectedTags.length} of 5 tags selected</span><button onClick={() => setPanel(null)}>Done tagging</button></footer>}
              </>
            )}

            {panel === 'entity' && activeEntity && (
              <div className="entity-sheet">
                {activeEntity.type === 'person' ? (
                  <CastProfileHero
                    portrait={activePortrait}
                    profile={activeEditorialProfile}
                    record={personProfile}
                    displayName={activeEntity.label}
                    initials={activeEntity.initials}
                    onOpenPerson={openProfilePerson}
                  />
                ) : (
                  <div className={`entity-hero entity-hero--${activeEntity.type}`}>
                    <span className="entity-monogram">{activeEntity.initials}</span>
                    <span className="eyebrow">{activeEntity.type === 'relationship' ? 'COUPLE JOURNEY' : activeEntity.type.toUpperCase()}</span>
                    <h2>{activeEntity.label}</h2>
                    <p>{activeEntity.type === 'relationship' ? 'Explore their cast connections, credited seasons and community conversation.' : activeEntity.detail}</p>
                  </div>
                )}
                <div className="entity-body">
                  {activeEntity.type === 'person' ? (
                    <CastProfileContent
                      profile={activeEditorialProfile}
                      portrait={activePortrait}
                      displayName={activeEntity.label}
                      record={personProfile}
                      loading={personProfileLoading}
                      error={personProfileError}
                      onOpenPerson={openProfilePerson}
                      onOpenRelationship={openProfileRelationship}
                      onOpenEpisode={openProfileEpisode}
                    />
                  ) : (
                    <CatalogEntityContent item={activeEntity} onOpenPerson={openProfilePerson} onOpenEpisode={openProfileEpisode} onClose={() => setPanel(null)} />
                  )}
                  <div className="entity-actions">
                    {activeEntity.type !== 'episode' && (
                      <button
                        className={`entity-follow ${followedTargets.has(`${activeEntity.type}:${activeEntity.id}`) ? 'is-following' : ''}`}
                        onClick={() => toggleFollow(activeEntity.type === 'person' ? 'person' : 'relationship', activeEntity.id, activeEntity.label)}
                      >
                        {followedTargets.has(`${activeEntity.type}:${activeEntity.id}`) ? <UserCheck size={17} /> : <UserPlus size={17} />}
                        {followedTargets.has(`${activeEntity.type}:${activeEntity.id}`) ? 'Following' : 'Follow'}
                      </button>
                    )}
                    <button className="entity-action" onClick={() => { toggleTag(activeEntity); setPanel(null); openComposer() }}><Tag size={17} /> Tag in a new post</button>
                  </div>
                </div>
              </div>
            )}

            {panel === 'live' && (
              <div className="live-shell">
                <nav className="live-room-tabs" aria-label="Choose an episode room">
                  <button className="live-room-back" type="button" onClick={openLiveRooms}><ArrowLeft size={14} />All live rooms</button>
                  {launchShows.map((show) => (
                    <button type="button" key={show.id} className={activeLiveShow.roomSlug === show.roomSlug ? 'is-active' : ''} onClick={() => setActiveLiveShow(show)}>
                      <span>{show.nightLabel}</span><strong>{show.title}</strong>
                    </button>
                  ))}
                </nav>
                <div className="live-room">
                  <div className={`live-room-stage live-room-stage--${activeLiveShow.accent}`}>
                    <Radio size={48} />
                    <h2>{activeLiveShow.title}{activeLiveShow.season !== null && ` · S${activeLiveShow.season}`}</h2>
                    <p>Episode conversation, live reactions and post-show discussion</p>
                    <div className="stage-countdown"><RoomCountdown loaded={liveRoomLoaded} publicCanPost={livePublicCanPost} opensAt={livePostingOpensAt} locksAt={livePostingLocksAt} state={liveRoomStatus} /></div>
                    <div className="stream-note"><ExternalLink size={15} /><span>Journey90 does not rebroadcast the episode. Watch through your own legal provider while chatting here.</span></div>
                  </div>
                  <div className="live-chat">
                    <header><div><span className="eyebrow">EPISODE ROOM</span><strong>{activeLiveShow.nightLabel} conversation</strong></div><span>{liveMessages.length} messages</span></header>
                    <aside className="chat-room-guidelines" aria-label="Room guidelines">
                      <strong><Heart size={13} />{roomGuidelines.heading}</strong>
                      <p>{roomGuidelines.body}</p>
                    </aside>
                    <div className="chat-messages" aria-live="polite">
                      {liveMessages.length === 0 && <div className="chat-empty"><Radio size={18} /><span>{liveCanPost ? 'Be the first fan to check in.' : 'Read the room now; join the chat when member posting opens.'}</span></div>}
                      {liveMessages.map((message) => <div className="chat-message" key={message.id}>
                        <Avatar initials={message.initials} imageUrl={message.avatarUrl} tone={message.tone} small />
                        <p><strong>{message.name}{message.sentAt && <small> · {message.sentAt}</small>}</strong><span>{message.body}</span>{Boolean(message.reactionCount) && <span aria-label={`${message.reactionCount} reactions`}>☺ {message.reactionCount}</span>}</p>
                        {currentMember?.role === 'admin' && message.authorId && message.reactable && <button type="button" className="admin-content-control" aria-label={`Moderate message from ${message.name}`} title="Remove message or block member" onClick={() => openModeration({kind:'live_message',id:message.id,authorId:message.authorId,authorName:message.name,body:message.body})}><MoreHorizontal size={18} /></button>}
                      </div>)}
                    </div>
                    <p className={`chat-posting-notice${liveAdminAccess ? ' chat-posting-notice--admin' : ''}`} role="status">
                      {livePostingNotice({ loaded: liveRoomLoaded, canPost: liveCanPost, adminAccess: liveAdminAccess, opensAt: livePostingOpensAt, state: liveRoomStatus })}
                    </p>
                    {!currentMember && liveCanPost ? (
                      <div className="member-gate member-gate--chat"><LockKeyhole size={17} /><span><strong>Join the live conversation</strong><small>Sign in with an email code to react and chat.</small></span><button type="button" onClick={() => beginAuth('live')}>Join or log in</button></div>
                    ) : (
                      <form className="chat-composer" onSubmit={(event) => { event.preventDefault(); void sendLiveMessage() }}>
                        <button
                          type="button"
                          aria-label={latestReactableMessage ? `React to the latest message from ${latestReactableMessage.name}` : 'No message available to react to'}
                          aria-pressed={latestReactableMessage?.reacted ?? false}
                          disabled={!latestReactableMessage || !liveCanPost || liveReacting}
                          onClick={() => { void toggleLatestLiveReaction() }}
                        ><Smile size={18} fill={latestReactableMessage?.reacted ? 'currentColor' : 'none'} /></button>
                        <input
                          aria-label="Room message"
                          placeholder={!liveCanPost ? 'This room is not open for messages yet' : 'Say something to the room…'}
                          value={liveMessage}
                          disabled={!liveCanPost}
                          maxLength={1000}
                          onChange={(event) => setLiveMessage(event.target.value)}
                        />
                        <button aria-label="Send message" disabled={!liveMessage.trim() || liveSending || !liveCanPost}><Send size={17} /></button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            )}

            {panel === 'comments' && activePost && (
              <div className="comments-sheet">
                <header className="comments-heading"><span className="eyebrow">POST CONVERSATION</span><h2>Replies</h2><p>Use Reply on a comment to respond directly and notify its author.</p></header>
                <article className="comments-origin">
                  <Avatar initials={activePost.avatar} imageUrl={activePost.avatarUrl} tone={activePost.tone} small />
                  <div><strong>{activePost.name}</strong><span>{activePost.handle} · {activePost.time}</span><p>{activePost.body}</p></div>
                </article>
                <div className="comments-list">
                  {commentsLoading && <div className="feed-state"><span className="feed-spinner" />Loading replies…</div>}
                  {commentsError && <p className="comments-error" role="alert">{commentsError}</p>}
                  {!commentsLoading && !commentsError && comments.length === 0 && <div className="comments-empty">No replies yet. Start the conversation.</div>}
                  {comments.map((comment) => (
                    <article className={`comment-row${comment.id === highlightedCommentId ? ' is-notification-target' : ''}`} id={`comment-${comment.id}`} tabIndex={-1} key={comment.id}>
                      <Avatar initials={comment.author.initials} imageUrl={comment.author.avatarUrl} tone="violet" small />
                      <div className="comment-content"><strong>{comment.author.name}</strong><small>{comment.author.handle} · {comment.postedAt}</small>
                        {comment.parentCommentId && <span className="comment-parent-context">Replying to {comments.find(parent => parent.id === comment.parentCommentId)?.author.name ?? 'an earlier comment'}</span>}
                        <p>{comment.body}</p>
                        <div className="comment-actions">
                          <button type="button" className="comment-reply-action" aria-label={`Reply to comment by ${comment.author.name}`} disabled={commentBusy || commentsLoading} onClick={() => startCommentReply(comment)}><MessageCircle size={13} />Reply</button>
                          {comment.author.id === currentMember?.id && <div className="comment-own-actions">
                            <button type="button" disabled={commentBusy} onClick={() => { void deleteOwnComment(comment) }}>{confirmCommentDelete === comment.id ? 'Confirm remove reply' : 'Remove reply'}</button>
                            {confirmCommentDelete === comment.id && <button type="button" disabled={commentBusy} onClick={() => setConfirmCommentDelete(null)}>Cancel</button>}
                          </div>}
                        </div>
                      </div>
                      {currentMember?.role === 'admin' && comment.author.id !== currentMember.id && <button type="button" className="admin-content-control" aria-label={`Moderate reply by ${comment.author.name}`} title="Remove reply or block member" onClick={() => openModeration({kind:'comment',id:comment.id,authorId:comment.author.id,authorName:comment.author.name,body:comment.body})}><MoreHorizontal size={18} /></button>}
                    </article>
                  ))}
                </div>
                {currentMember ? (
                  <form className="comment-composer" onSubmit={(event) => { event.preventDefault(); void publishComment() }}>
                    {selectedCommentReply && <div className="comment-reply-target" role="status">
                      <span><strong>Replying to {selectedCommentReply.authorName}</strong><small>{selectedCommentReply.body.slice(0, 120)}{selectedCommentReply.body.length > 120 ? '…' : ''}</small></span>
                      <button type="button" aria-label="Cancel direct reply" disabled={commentBusy} onClick={cancelCommentReply}><X size={14} />Cancel</button>
                    </div>}
                    <Avatar initials={currentMember.initials} imageUrl={currentMember.avatarUrl} tone="lime" small />
                    <textarea ref={commentReplyRef} aria-label="Write a reply" aria-describedby={selectedCommentReply ? 'comment-reply-help' : undefined} value={commentDraft} maxLength={2000} disabled={commentBusy || commentsLoading} placeholder={selectedCommentReply ? `Reply to ${selectedCommentReply.authorName}…` : `Comment on ${activePost.name}'s post…`} onChange={(event) => setCommentDraft(event.target.value)} />
                    <button disabled={!commentDraft.trim() || commentBusy || commentsLoading}><Send size={16} />{commentBusy ? 'Saving…' : 'Reply'}</button>
                    {selectedCommentReply && <small className="comment-reply-help" id="comment-reply-help">Reply alerts go to the comment’s author and post owner, excluding you.</small>}
                  </form>
                ) : (
                  <div className="member-gate"><LockKeyhole size={17} /><span><strong>Ready to reply?</strong><small>Join with your email—no password required.</small></span><button type="button" onClick={() => beginAuth('reply')}>Join or log in</button></div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {optionsPostId && currentMember && (
        <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !postActionBusy) setOptionsPostId(null) }}>
          <section className="modal-card post-options-dialog" role="dialog" aria-modal="true" aria-label="Your post options">
            <button className="modal-close" aria-label="Close post options" disabled={postActionBusy} onClick={() => setOptionsPostId(null)}><X size={20}/></button>
            <h2>Your post</h2>
            <label htmlFor="edit-post-body">Edit post text</label>
            <textarea id="edit-post-body" value={editPostBody} maxLength={5000} disabled={postActionBusy} onChange={(event) => { setEditPostBody(event.target.value); setConfirmPostDelete(false) }}/>
            <p>Linked tags and quote context stay unchanged.</p>
            {confirmPostDelete && <p role="alert">Remove this post from the community feed? Other fans will no longer see it.</p>}
            <footer>
              <button type="button" disabled={postActionBusy} onClick={() => setOptionsPostId(null)}>Cancel</button>
              <button type="button" disabled={postActionBusy} onClick={() => { void deleteOwnPost() }}>{confirmPostDelete ? 'Confirm remove post' : 'Remove post'}</button>
              <button type="button" disabled={postActionBusy || !editPostBody.trim()} onClick={() => { void saveOwnPost() }}>{postActionBusy ? 'Saving…' : 'Save post'}</button>
            </footer>
          </section>
        </div>
      )}

      {currentMember && (
        <EditProfileDialog
          open={editProfileOpen}
          member={currentMember}
          onClose={() => setEditProfileOpen(false)}
          onMemberChange={applyProfileMember}
          onSaved={() => {
            setFeedRevision((current) => current + 1)
            setToast('Profile updated')
          }}
        />
      )}

      {currentMember?.role === 'admin' && <AdminModerationDialog
        key={currentMember.id}
        open={moderationOpen}
        target={moderationTarget}
        currentAdminId={currentMember.id}
        onClose={() => { setModerationOpen(false); setModerationTarget(null) }}
        onCompleted={applyModeration}
      />}

      <AuthDialog
        open={authOpen}
        resumeProfileMember={incompleteMember}
        onProfilePending={setIncompleteMember}
        onClose={() => {
          setAuthOpen(false)
          setPendingAuthIntent(null)
          pendingAuthActionRef.current = null
        }}
        onAuthenticated={finishAuthentication}
      />

      {toast && <div className="toast" role="status"><Check size={17} />{toast}</div>}
    </div>
  )
}

export default App
